import * as qz from "qz-tray";
import { sha256 } from "js-sha256";
import * as moment from "moment";
import Printer from "./print/printer";
import { shouldUseRedModifiers } from "./print/impactPrinter";
import { hextorstr, KEYUTIL, KJUR, stob64 } from "jsrsasign";
import Vue from "vue";
import groupBy from "lodash.groupby";
import store from "../store/store";
import { sendToKds, sendOrderCancellationToKds } from "./kds/kdsHelper";
import { getOrderFinalTotal } from "../pricing";
import { getCashDiscountFactor } from "./order/cashDiscount";

/**
 * Load QR code library from CDN
 */
async function loadQRCodeLibrary() {
  return new Promise((resolve, reject) => {
    if (typeof window.qrcode !== 'undefined' || typeof window.QRCode !== 'undefined') {
      resolve();
      return;
    }

    const libraries = [
      {
        url: 'https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js',
        check: () => typeof window.qrcode !== 'undefined'
      },
      {
        url: 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/js/qrcode.js',
        check: () => typeof window.qrcode !== 'undefined'
      },
      {
        url: 'https://unpkg.com/qrcode-generator@1.4.4/js/qrcode.js',
        check: () => typeof window.qrcode !== 'undefined'
      }
    ];

    let currentIndex = 0;

    const tryNextLibrary = () => {
      if (currentIndex >= libraries.length) {
        reject(new Error('Failed to load any QR code library'));
        return;
      }

      const library = libraries[currentIndex];
      const script = document.createElement('script');
      script.src = library.url;
      script.onload = () => {
        if (library.check()) {
          resolve();
        } else {
          currentIndex++;
          tryNextLibrary();
        }
      };
      script.onerror = () => {
        currentIndex++;
        tryNextLibrary();
      };
      document.head.appendChild(script);
    };

    tryNextLibrary();
  });
}

/**
 * Generate QR code as data URL
 */
async function generateQRCodeDataURL(text, size = 128) {
  try {
    await loadQRCodeLibrary();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = size;
    canvas.height = size;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, size, size);

    if (typeof window.qrcode !== 'undefined' && window.qrcode) {
      const qr = window.qrcode(0, 'M');
      qr.addData(text);
      qr.make();

      const moduleCount = qr.getModuleCount ? qr.getModuleCount() : 21;
      const cellSize = Math.floor(size / moduleCount);
      const offset = Math.floor((size - moduleCount * cellSize) / 2);

      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (qr.isDark && qr.isDark(row, col)) {
            ctx.fillStyle = '#000000';
            ctx.fillRect(col * cellSize + offset, row * cellSize + offset, cellSize, cellSize);
          }
        }
      }

      return canvas.toDataURL('image/png');
    }

    throw new Error('QR code library not available');
  } catch (error) {
    console.error('QR code generation failed:', error);
    throw error;
  }
}

/**
 * Get logo data URL from cache or fetch it
 */
async function getLogoDataUrl(url) {
  if (!url) return null;

  const cacheKey = `cached_store_logo_${url}`;
  const cached = localStorage.getItem(cacheKey);

  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;
        try {
          localStorage.setItem(cacheKey, base64data);
        } catch (e) {
          console.warn('Failed to cache logo:', e);
        }
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch store logo:', error);
    return null;
  }
}

/**
 * Build complete receipt as HTML (black and white only)
 */
async function buildHTMLReceipt(orderObject, typeOfReceipt = null, showQRCode = false) {
  const terminalInfo = store.getters["config/terminal"];
  const storeInfo = store.getters["config/store"];
  const currentEmployee = store.getters["employee"];
  const currency = store.getters["config/currency"];

  // Get logo if available
  let logoDataUrl = null;
  if (storeInfo.logo) {
    logoDataUrl = await getLogoDataUrl(storeInfo.logo);
  }

  // Get db_settings
  const dbSettings = terminalInfo["db_settings"] ? JSON.parse(JSON.stringify(terminalInfo["db_settings"])) : [];
  const showSignatureLine = dbSettings.find(setting => setting.name === "receipt_signature_line")?.value === "true";
  const showTipLine = dbSettings.find(setting => setting.name === "receipt_tip_line")?.value === "true";
  const showTipSuggestions = dbSettings.find(setting => setting.name === "receipt_tip_suggest")?.value === "true";
  const showCashDiscount = dbSettings.find(setting => setting.name === "cash_discount")?.value === "true";

  // Localization
  const localization = store.getters["config/isLocalization"];
  const localizedFormat = "MM/DD/YYYY";
  const deLocalizedFormat = "DD/MM/YYYY";
  const date = moment(new Date()).format(localization ? localizedFormat : deLocalizedFormat);
  const time = moment(new Date()).format("hh:mm A");
  const tableName = orderObject.table ? (store.getters["config/getTableById"](orderObject.table))?.table_name || null : null;

  // Generate QR code if needed
  let qrDataURL = null;
  let qrPending = false;
  let atcudValue = null;
  if (showQRCode) {
    // Default QR code value - commented out, should come from backend
    // const qrContent = "A:517663210*B:999999990*C:PT*D:FR*E:N*F:20251104*G:FR M/1*H:J6FS4C6R-1*I1:PT*I5:3.00*I6:0.39*N:0.39*O:3.39*Q:BuhZ*R:2860*S:TB;3.39";

    // Try to fetch Moloni QR code from backend
    let qrContent = null;
    try {
      const ApiService = await import('@/core/services/api.service');
      const response = await ApiService.default.get(`orders/${orderObject.id}/meta/`);

      // Response is a flat object with meta keys as properties
      if (response.data && response.data.moloni_invoice_number) {
        qrContent = response.data.moloni_invoice_number;

        // Extract ATCUD (H value) from QR code data
        // Format: "A:xxx*B:xxx*...*H:J6FS4C6R-30*..."
        const hMatch = qrContent.match(/\*H:([^*]+)/);
        if (hMatch && hMatch[1]) {
          atcudValue = hMatch[1];
        }
      } else {
        qrPending = true;
      }
    } catch (error) {
      console.warn('Failed to fetch Moloni QR code, showing pending message:', error);
      qrPending = true;
    }

    // Generate QR code if we have content
    if (qrContent) {
      try {
        qrDataURL = await generateQRCodeDataURL(qrContent, 120);
      } catch (error) {
        console.error("Failed to generate QR code:", error);
        qrPending = true;
      }
    }
  }

  // Build HTML - matching exact template design
  let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Receipt</title>
  <style>
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      margin: 0;
      padding: 0;
      color: #000;
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: bold; }
    .small { font-size: 10px; }
    table { width: 100%; border-collapse: collapse; }
    th { font-size: 10px; border-bottom: 1px solid #000; padding-bottom: 2px; }
    td { padding: 2px 0; vertical-align: top; }
    .separator { border-top: 1px dotted #000; margin: 6px 0; }
    .items td:first-child { width: 55%; }
    .qr img { 
      width: 120px; 
      height: 120px;
      image-rendering: pixelated;
      image-rendering: -moz-crisp-edges;
      image-rendering: crisp-edges;
    }
  </style>
</head>
<body>
<div class="center">
  ${logoDataUrl ? `<div style="margin-bottom: 10px;"><img src="${logoDataUrl}" style="max-width: 250px; max-height: 100px;"></div>` : ''}
  <div>${storeInfo.name}</div>
`;

  if (storeInfo.address) html += `  <div>${storeInfo.address}</div>\n`;
  if (storeInfo.address_2) html += `  <div>${storeInfo.address_2}</div>\n`;
  if (storeInfo.phone) html += `  <div>Phone: ${storeInfo.phone}</div>\n`;
  if (storeInfo.fax) html += `  <div>Fax: ${storeInfo.fax}</div>\n`;
  if (storeInfo.email) html += `  <div>Email: ${storeInfo.email}</div>\n`;
  if (storeInfo.website) html += `  <div>${storeInfo.website}</div>\n`;
  html += `</div>\n`;
  html += `\n<div class="separator"></div>\n\n`;

  // Customer info
  if (orderObject["customer"]) {
    html += `<div class="center">\n`;
    html += `  <div class="bold">Customer</div>\n`;
    if (orderObject["customer"].first_name || orderObject["customer"].last_name) {
      html += `  <div>${orderObject["customer"].first_name || ''} ${orderObject["customer"].last_name || ''}</div>\n`;
    } else {
      html += `  <div>Walk-in Customer</div>\n`;
    }
    if (orderObject["customer"].phone) {
      html += `  <div>Phone: ${orderObject["customer"].phone}</div>\n`;
    }
    html += `</div>\n`;
    html += `<div class="separator"></div>\n\n`;
  }

  // Refund receipt
  if (typeOfReceipt === "refund") {
    html += `<div class="center bold">*** REFUND RECEIPT ***</div>\n`;
    html += `<div class="separator"></div>\n\n`;
  }

  // Header text
  if (storeInfo.receipt_header_text) {
    html += `<div class="center">${storeInfo.receipt_header_text}</div>\n`;
    html += `<div class="separator"></div>\n\n`;
  }

  // Receipt info table
  html += `<table>\n`;
  html += `  <tr>\n`;
  // Show different receipt title based on whether order has payments
  const hasPayments = orderObject.payments && orderObject.payments.length > 0;
  const receiptTitle = hasPayments
    ? "Processado por programa certificado no 1455/AT"
    : "Consumo de mesa";
  html += `    <td class="bold small">${receiptTitle}</td>\n`;
  html += `    <td class="right small">#${orderObject.id}</td>\n`;
  html += `  </tr>\n`;
  html += `  <tr>\n`;
  html += `    <td class="small">Date: ${date} ${time}</td>\n`;
  html += `    <td class="right small">${typeOfReceipt === "refund" ? 'REFUND' : 'Original'}</td>\n`;
  html += `  </tr>\n`;

  if (currentEmployee) {
    const staffName = currentEmployee.name.split(' ')[0] + (currentEmployee.name.split(' ').length > 1 ? ' ' + currentEmployee.name.split(' ')[currentEmployee.name.split(' ').length - 1].charAt(0) + '.' : '');
    html += `  <tr><td class="small" colspan="2">Staff: ${staffName}</td></tr>\n`;
  }

  if (tableName) {
    html += `  <tr><td class="small" colspan="2">Table: ${tableName}</td></tr>\n`;
  }

  if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    const diningOptionName = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name;
    html += `  <tr><td class="small" colspan="2">Dining Option: ${diningOptionName}</td></tr>\n`;
  }

  html += `</table>\n`;
  html += `<div class="separator"></div>\n`;
  html += `<table class="items">\n`;
  html += `  <tr>\n`;
  html += `    <th class="center">QTD.</th>\n`;
  html += `    <th class="center">PRICE</th>\n`;
  html += `    <th class="center">TAX %</th>\n`;
  html += `    <th class="center">TOTAL</th>\n`;
  html += `  </tr>\n`;

  // Items
  for (const item of orderObject.items) {
    if (!item.void_status) {
      let displayName = item.name;
      const productSubtotal = item.price * Math.abs(item.quantity);
      const itemTotal = item.subtotal || productSubtotal;

      // Calculate tax rate for this item
      let taxRate = 0;
      if (item.tax_items && item.tax_items.length > 0) {
        // Try to extract tax rate from the first tax item
        const firstTax = item.tax_items[0];
        const rateMatch = firstTax.name.match(/(\d+(\.\d+)?)\s*%/);
        if (rateMatch) {
          taxRate = parseFloat(rateMatch[1]);
        } else {
          // Calculate from tax amount and item price
          const itemBase = productSubtotal;
          if (itemBase > 0) {
            taxRate = (firstTax.tax_total / itemBase) * 100;
          }
        }
      }

      html += `  <tr>\n`;
      html += `    <td>${displayName}<br><span class="small">${Math.abs(item.quantity).toFixed(5)} uni</span>`;

      // Add modifiers inline
      if (item.modifiers && item.modifiers.length > 0) {
        for (const modifierGroup of await groupingModifiers(item.product, item.modifiers)) {
          for (const modifier of modifierGroup.modifiers) {
            if (!modifier.void_status) {
              html += `<br><span class="small">+ ${modifier.name}</span>`;
            }
          }
        }
      }

      // Add notes
      if (item.notes) {
        html += `<br><span class="small">Note: ${item.notes}</span>`;
      }

      // Add discount
      if (item.discount && item.discount > 0) {
        let discountText = "Discount";
        if (item.discount_type === 1 && item.discount_percentage) {
          discountText = `Discount ${item.discount_percentage}%`;
        }
        const totalItemDiscount = item.discount * Math.abs(item.quantity);
        html += `<br><span class="small">${discountText}: -${currency}${totalItemDiscount.toFixed(2)}</span>`;
      }

      html += `</td>\n`;
      html += `    <td class="right">${item.price.toFixed(5)}</td>\n`;
      html += `    <td class="right">${taxRate.toFixed(2)}</td>\n`;
      html += `    <td class="right">${itemTotal.toFixed(2)}</td>\n`;
      html += `  </tr>\n`;
    }
  }

  html += `</table>\n`;
  html += `<div class="separator"></div>\n`;

// Calculate pre-discount subtotal
let preDiscountOrderSubtotal = 0;
for (const item of orderObject.items) {
  if (!item.void_status) {
    preDiscountOrderSubtotal += item.price * Math.abs(item.quantity);
    if (item.modifiers) {
      for (const modifier of item.modifiers) {
        if (!modifier.void_status) {
          preDiscountOrderSubtotal += modifier.price * Math.abs(item.quantity);
        }
      }
    }
  }
}

// Taxes
let allTaxes = [];
orderObject.items.filter(item => !item.void_status).forEach((item) => {
  if (item.tax_items) {
    allTaxes = allTaxes.concat(item.tax_items);
  }
});
const byName = groupBy(allTaxes, it => it["name"]);
const calculatedTax = Object.keys(byName).map(name => {
  const sum = byName[name].reduce((acc, it) => acc + it.tax_total, 0);
  return { tax_name: name, total: sum };
});

const itemLevelTax = calculatedTax.reduce((sum, tax) => sum + tax.total, 0);
const totalTax = itemLevelTax > 0 ? itemLevelTax : (orderObject.tax_total || 0);
const netAmount = preDiscountOrderSubtotal - (orderObject.discount_total || 0);
const finalTotal = getOrderFinalTotal(orderObject);

  // Totals section
  html += `<table>\n`;
  html += `  <tr><td>Subtotal</td><td class="right">${preDiscountOrderSubtotal.toFixed(2)}</td></tr>\n`;

  if (orderObject.discount_total && orderObject.discount_total > 0) {
    html += `  <tr><td>Total discount</td><td class="right">${orderObject.discount_total.toFixed(2)}</td></tr>\n`;
  }

  html += `  <tr><td>Net amount</td><td class="right">${netAmount.toFixed(2)}</td></tr>\n`;

  if (totalTax > 0) {
    html += `  <tr><td>Total tax</td><td class="right">${totalTax.toFixed(2)}</td></tr>\n`;
  }

  if (orderObject.surcharge_value && orderObject.surcharge_value > 0) {
    html += `  <tr><td>Non-Cash Fee</td><td class="right">${orderObject.surcharge_value.toFixed(2)}</td></tr>\n`;
  }

  if (orderObject.tips_total && orderObject.tips_total > 0) {
    html += `  <tr><td>Tip</td><td class="right">${orderObject.tips_total.toFixed(2)}</td></tr>\n`;
  }

  html += `</table>\n`;
  if (showCashDiscount) {
    const CASH_DISCOUNT_FACTOR = getCashDiscountFactor(dbSettings);
    const nonCashTotal = orderObject.cash_discount ? finalTotal * CASH_DISCOUNT_FACTOR : finalTotal;
    const cashTotal = orderObject.cash_discount ? finalTotal : finalTotal / CASH_DISCOUNT_FACTOR;
    html += `<div class="center bold" style="margin: 8px 0; font-size: 13px;">\n`;
    html += `  Total (Non-Cash): ${nonCashTotal.toFixed(2)} ${currency}<br>\n`;
    html += `  Total (Cash): ${cashTotal.toFixed(2)} ${currency}\n`;
    html += `</div>\n`;
  } else {
    html += `<div class="center bold" style="margin: 8px 0; font-size: 13px;">\n`;
    html += `  Total: ${finalTotal.toFixed(2)} ${currency}\n`;
    html += `</div>\n`;
  }
  // Payments
  if (orderObject.payments.length > 0) {
    html += `<table>\n`;
    orderObject.payments.forEach(function (payment) {
      const tenderedAmount = orderObject.surcharge_value ? payment.amount + orderObject.surcharge_value : payment.amount;
      html += `  <tr>\n`;
      html += `    <td>${payment.payment_type}</td>\n`;
      html += `    <td class="right">${tenderedAmount.toFixed(2)}</td>\n`;
      html += `  </tr>\n`;

      if (payment.change_amount && payment.change_amount > 0) {
        html += `  <tr>\n`;
        html += `    <td class="small">Change</td>\n`;
        html += `    <td class="right small">${payment.change_amount.toFixed(2)}</td>\n`;
        html += `  </tr>\n`;
      }

      let _htmlIposhpData = null;
      if (payment.processor_response && payment.processor_response !== 'no data') {
        try {
          const _p = JSON.parse(payment.processor_response);
          _htmlIposhpData = _p.iposhpresponse || _p.iposTransactResponse || null;
        } catch (e) {
          try {
            const _f = payment.processor_response.replace(/'/g, '"').replace(/\bNone\b/g, 'null').replace(/\bTrue\b/g, 'true').replace(/\bFalse\b/g, 'false');
            const _p2 = JSON.parse(_f);
            _htmlIposhpData = _p2.iposhpresponse || _p2.iposTransactResponse || null;
          } catch (e2) { /* non-critical */ }
        }
      }

      const isCreditCardPayment = (payment.payment_type &&
        (payment.payment_type.toLowerCase().includes('credit') ||
         payment.payment_type.toLowerCase().includes('card') ||
         payment.payment_type.toLowerCase().includes('dejavoo') ||
         payment.payment_type.toLowerCase().includes('nabancard'))) ||
        !!_htmlIposhpData;

      if (payment.card_lastfour) {
        html += `  <tr>\n`;
        html += `    <td class="small">Card: **** ${payment.card_lastfour}</td>\n`;
        html += `    <td></td>\n`;
        html += `  </tr>\n`;
      }

      if (payment.auth_code) {
        html += `  <tr>\n`;
        html += `    <td class="small">Auth: ${payment.auth_code}</td>\n`;
        html += `    <td></td>\n`;
        html += `  </tr>\n`;
      }

      // Tip line
      if (showTipLine && isCreditCardPayment) {
        html += `  <tr>\n`;
        html += `    <td class="small">TIP:</td>\n`;
        html += `    <td class="right small">____________</td>\n`;
        html += `  </tr>\n`;
        html += `  <tr>\n`;
        html += `    <td class="small">TOTAL:</td>\n`;
        html += `    <td class="right small">____________</td>\n`;
        html += `  </tr>\n`;
      }

      // Signature line
      if (showSignatureLine && isCreditCardPayment) {
        html += `  <tr>\n`;
        html += `    <td colspan="2" class="small" style="padding-top: 10px;">X ___________________________</td>\n`;
        html += `  </tr>\n`;
        html += `  <tr>\n`;
        html += `    <td colspan="2" class="center small">SIGNATURE</td>\n`;
        html += `  </tr>\n`;
      }

      // Tip suggestions
      if (showTipSuggestions && isCreditCardPayment) {
        const baseAmount = orderObject.surcharge_value ?
          parseFloat(payment.amount) + parseFloat(orderObject.surcharge_value) :
          parseFloat(payment.amount);

        if (baseAmount > 0) {
          [15, 18, 20].forEach(percentage => {
            const tipAmount = Math.round(baseAmount * (percentage / 100) * 100) / 100;
            const totalWithTip = baseAmount + tipAmount;
            html += `  <tr>\n`;
            html += `    <td class="small">${percentage}% tip: ${tipAmount.toFixed(2)}</td>\n`;
            html += `    <td class="right small">Total: ${totalWithTip.toFixed(2)}</td>\n`;
            html += `  </tr>\n`;
          });
        }
      }
    });
    html += `</table>\n`;
  }

  // Tax breakdown table
  if (calculatedTax.length > 0) {
    html += `<div class="separator"></div>\n`;
    html += `<table>\n`;
    html += `  <tr>\n`;
    html += `    <th class="small">DESCRIPTION</th>\n`;
    html += `    <th class="small right">TAX</th>\n`;
    html += `    <th class="small right">BASE</th>\n`;
    html += `    <th class="small right">AMOUNT</th>\n`;
    html += `  </tr>\n`;

    calculatedTax.forEach((tax) => {
      // Calculate base amount for this tax
      let taxBase = 0;
      orderObject.items.filter(item => !item.void_status).forEach((item) => {
        if (item.tax_items) {
          item.tax_items.forEach(taxItem => {
            if (taxItem.name === tax.tax_name) {
              taxBase += (item.price * Math.abs(item.quantity));
              if (item.modifiers) {
                item.modifiers.filter(m => !m.void_status).forEach(modifier => {
                  taxBase += (modifier.price * Math.abs(item.quantity));
                });
              }
            }
          });
        }
      });

      // Try to extract tax rate (assuming it might be in the name like "Tax 13%")
      const rateMatch = tax.tax_name.match(/(\d+(\.\d+)?)\s*%/);
      const taxRate = rateMatch ? rateMatch[1] : '0';

      html += `  <tr>\n`;
      html += `    <td>${tax.tax_name}</td>\n`;
      html += `    <td class="right">${taxRate}</td>\n`;
      html += `    <td class="right">${taxBase.toFixed(2)}</td>\n`;
      html += `    <td class="right">${tax.total.toFixed(2)}</td>\n`;
      html += `  </tr>\n`;
    });

    html += `</table>\n`;
  }

  // Receipt ID
  html += `<div class="center small" style="margin-top: 8px;">\n`;
  html += `  ${orderObject.id}\n`;
  html += `</div>\n`;

  // QR Code
  if (showQRCode) {
    html += `<div class="separator"></div>\n`;
    if (qrDataURL) {
      // Display ATCUD text above QR code
      if (atcudValue) {
        html += `<div class="center" style="margin: 8px 0; font-size: 11px; font-weight: bold;">\n`;
        html += `  ATCUD:${atcudValue}\n`;
        html += `</div>\n`;
      }
      html += `<div class="center qr">\n`;
      html += `  <img src="${qrDataURL}" alt="QR code">\n`;
      html += `</div>\n`;
    } else if (qrPending) {
      html += `<div class="center" style="margin: 16px 0; font-size: 12px; font-weight: bold;">\n`;
      html += `  Receipt QR - Pending\n`;
      html += `</div>\n`;
    }
  }

  // Footer
  if (storeInfo.receipt_footer_text) {
    html += `<div class="center small">\n`;
    html += `  ${storeInfo.receipt_footer_text}\n`;
    html += `</div>\n`;
  }

  // Always show Powered by footer
  html += `<div class="center small" style="margin-top: 4px;">\n`;
  html += `  Powered by Biyo POS | biyo.de\n`;
  html += `</div>\n`;

  html += `</body>\n`;
  html += `</html>\n`;

  return html;
}

/**
 * Print complete receipt as HTML using QZ Tray pixel printing
 */
async function printHTMLReceipt(orderObject, typeOfReceipt = null, showQRCode = false, customPrinter = null) {
  try {
    // Build HTML content
    const htmlContent = await buildHTMLReceipt(orderObject, typeOfReceipt, showQRCode);

    // Use QZ Tray HTML printing format
    const data = [{
      type: 'pixel',
      format: 'html',
      flavor: 'plain',
      data: htmlContent
    }];

    const terminal = store.getters['config/terminal'];
    let confRawObj, currentPrinter;

    if (customPrinter) {
      currentPrinter = customPrinter;
    } else {
      currentPrinter = terminal?.printer;
    }

    if (!currentPrinter) {
      throw new Error('No printer configured');
    }

    if (currentPrinter.local_ip) {
      confRawObj = {host: currentPrinter.local_ip, port: 9100};
    } else {
      confRawObj = {name: currentPrinter.name};
    }

    const config = qz.configs.create(confRawObj);
    const connectOptions = terminal.printer_server_ip ? {...{host: terminal.printer_server_ip}} : {};

    if (!qz.websocket.isActive()) {
      await qz.websocket.connect(connectOptions);
    }

    // Send HTML print job
    await qz.print(config, data);
  } catch (error) {
    console.error('Failed to print HTML receipt:', error);
    throw error;
  }
}

const defaultWidthPercent = 0.95;

qz.api.setSha256Type(data => sha256(data));
qz.api.setPromiseType(resolver => new Promise(resolver));
qz.security.setCertificatePromise(function (resolve) {

  //Alternate method 2 - direct
  resolve(`-----BEGIN CERTIFICATE-----
MIIFCDCCAvCgAwIBAgIQNzkyMDI0MDcyMjIyNDYyNTANBgkqhkiG9w0BAQsFADCB
mDELMAkGA1UEBhMCVVMxCzAJBgNVBAgMAk5ZMRswGQYDVQQKDBJRWiBJbmR1c3Ry
aWVzLCBMTEMxGzAZBgNVBAsMElFaIEluZHVzdHJpZXMsIExMQzEZMBcGA1UEAwwQ
cXppbmR1c3RyaWVzLmNvbTEnMCUGCSqGSIb3DQEJARYYc3VwcG9ydEBxemluZHVz
dHJpZXMuY29tMB4XDTI0MDcyMjIyNDYyNVoXDTI2MDcyMjIzMzAyNFowgcMxCzAJ
BgNVBAYMAlVTMRMwEQYDVQQIDApOZXcgSmVyc2V5MRAwDgYDVQQHDAdQYXJhbXVz
MQ0wCwYDVQQKDARCaXlvMQ0wCwYDVQQLDARCaXlvMQ0wCwYDVQQDDARCaXlvMSIw
IAYJKoZIhvcNAQkBDBNtYXR0QGJpeW93YWxsZXQuY29tMTwwOgYDVQQNDDNyZW5l
d2FsLW9mLTBhNTZjMGZlZmVjYTc2ZDRmYzAxODE4NGU0ODQ3MDQ3ZDBmZWVmMjMw
ggEgMAsGCSqGSIb3DQEBAQOCAQ8AMIIBCgKCAQEAmOP8o8t6hj2A52OAQ08GjV1S
t0E08f681dL1QBjnarDuOMPFlvbnziR8n1c4fmODLNPdmJ1+ZOliTpVDkbD9xNFj
CoxHkpPOiGY5glGLdcI8LMy356F4R6hw+MDY1KLMfWC4K4X4N8/6bwjEU/9oqrtE
+lPcPUN3RilveER2gbjQyAt62CH/acYY1BASUQj0Ba8dvYflTqp0DMYm5C4Y9uuP
h1sML1cI5i6SBBRxWvFM8UfgIgu5tA3v/QGRV8GiEXcBoEwzMiXrZGldEx+wi5Eo
fkaAhmBsZo0TDp5+tFDK31m7ecwFEOtab2j3qI5yzKJVyPYJn6mb5cw/sz+iBQID
AQABoyMwITAfBgNVHSMEGDAWgBSQplC3hNS56l/yBYQTeEXoqXVUXDANBgkqhkiG
9w0BAQsFAAOCAgEAAVk/PHeFv2zBtxTErjeGl/IOijUrA1ZaW51lHiL59t/4U8Yw
fxMiavCvFJPtQV1KM9ei6BM/iWfHWRLKz4b+gdFrcdjoUgtd223PBQxzTHifhgaE
GoM9RGumxLrQULF+tjTf1+mO4NmsUZW9e6gulxnJ1mVbEQpsbG4m8w/BMqLONqxc
uZOkOjDsbDrFCfvmAM4qLgaXNMy2mllPJ44zSYgmfeYokgu0Z0jIoaEQP8lVR1b4
xTLaoe825Rag6cFwKVVwQ7jYE6t3ALpa7BbF+L5icb2X3hwTJX4hrb08tgwAmIPl
eC+ZtaS7/oKAzIsP5KBfAI3Keagp6q8/ekMFLPaHg3/FtahpFiGHM8maTBXjqZB6
ykzbbzlB7W+JsvX5JyE+Hkf4tR/kf7GWVa2WViGrBmVEvtCFRlMA0h5+ogGSPi1Y
84YIn9P0tedpageM4c/QzETm8fX0c8pMGBOiuoQgJyRTckJ85y+0rmhI033go0xb
VWZG+JFkubr/nE6cm96nNaK8z7M3IS3lHboVXUoodeQF7jsXnWj9lVEAfV1wClpZ
O4lWqF69D9J+1Wqn9CiM+no1eppk36hTjZSnRi9BoVC4nTqwVOLG6bU4vRMyeHpo
fSF1waNQ6GQfB9SgjkyBwbaToYVuL0cK0dDW/WAf0LcloHzuE15UeJ7iuFE=
-----END CERTIFICATE-----
--START INTERMEDIATE CERT--
-----BEGIN CERTIFICATE-----
MIIFEjCCA/qgAwIBAgICEAAwDQYJKoZIhvcNAQELBQAwgawxCzAJBgNVBAYTAlVT
MQswCQYDVQQIDAJOWTESMBAGA1UEBwwJQ2FuYXN0b3RhMRswGQYDVQQKDBJRWiBJ
bmR1c3RyaWVzLCBMTEMxGzAZBgNVBAsMElFaIEluZHVzdHJpZXMsIExMQzEZMBcG
A1UEAwwQcXppbmR1c3RyaWVzLmNvbTEnMCUGCSqGSIb3DQEJARYYc3VwcG9ydEBx
emluZHVzdHJpZXMuY29tMB4XDTE1MDMwMjAwNTAxOFoXDTM1MDMwMjAwNTAxOFow
gZgxCzAJBgNVBAYTAlVTMQswCQYDVQQIDAJOWTEbMBkGA1UECgwSUVogSW5kdXN0
cmllcywgTExDMRswGQYDVQQLDBJRWiBJbmR1c3RyaWVzLCBMTEMxGTAXBgNVBAMM
EHF6aW5kdXN0cmllcy5jb20xJzAlBgkqhkiG9w0BCQEWGHN1cHBvcnRAcXppbmR1
c3RyaWVzLmNvbTCCAiIwDQYJKoZIhvcNAQEBBQADggIPADCCAgoCggIBANTDgNLU
iohl/rQoZ2bTMHVEk1mA020LYhgfWjO0+GsLlbg5SvWVFWkv4ZgffuVRXLHrwz1H
YpMyo+Zh8ksJF9ssJWCwQGO5ciM6dmoryyB0VZHGY1blewdMuxieXP7Kr6XD3GRM
GAhEwTxjUzI3ksuRunX4IcnRXKYkg5pjs4nLEhXtIZWDLiXPUsyUAEq1U1qdL1AH
EtdK/L3zLATnhPB6ZiM+HzNG4aAPynSA38fpeeZ4R0tINMpFThwNgGUsxYKsP9kh
0gxGl8YHL6ZzC7BC8FXIB/0Wteng0+XLAVto56Pyxt7BdxtNVuVNNXgkCi9tMqVX
xOk3oIvODDt0UoQUZ/umUuoMuOLekYUpZVk4utCqXXlB4mVfS5/zWB6nVxFX8Io1
9FOiDLTwZVtBmzmeikzb6o1QLp9F2TAvlf8+DIGDOo0DpPQUtOUyLPCh5hBaDGFE
ZhE56qPCBiQIc4T2klWX/80C5NZnd/tJNxjyUyk7bjdDzhzT10CGRAsqxAnsjvMD
2KcMf3oXN4PNgyfpbfq2ipxJ1u777Gpbzyf0xoKwH9FYigmqfRH2N2pEdiYawKrX
6pyXzGM4cvQ5X1Yxf2x/+xdTLdVaLnZgwrdqwFYmDejGAldXlYDl3jbBHVM1v+uY
5ItGTjk+3vLrxmvGy5XFVG+8fF/xaVfo5TW5AgMBAAGjUDBOMB0GA1UdDgQWBBSQ
plC3hNS56l/yBYQTeEXoqXVUXDAfBgNVHSMEGDAWgBQDRcZNwPqOqQvagw9BpW0S
BkOpXjAMBgNVHRMEBTADAQH/MA0GCSqGSIb3DQEBCwUAA4IBAQAJIO8SiNr9jpLQ
eUsFUmbueoxyI5L+P5eV92ceVOJ2tAlBA13vzF1NWlpSlrMmQcVUE/K4D01qtr0k
gDs6LUHvj2XXLpyEogitbBgipkQpwCTJVfC9bWYBwEotC7Y8mVjjEV7uXAT71GKT
x8XlB9maf+BTZGgyoulA5pTYJ++7s/xX9gzSWCa+eXGcjguBtYYXaAjjAqFGRAvu
pz1yrDWcA6H94HeErJKUXBakS0Jm/V33JDuVXY+aZ8EQi2kV82aZbNdXll/R6iGw
2ur4rDErnHsiphBgZB71C5FD4cdfSONTsYxmPmyUb5T+KLUouxZ9B0Wh28ucc1Lp
rbO7BnjW
-----END CERTIFICATE-----`);
});

const privateKey = "-----BEGIN PRIVATE KEY-----\n" +
  "MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQCY4/yjy3qGPYDn\n" +
  "Y4BDTwaNXVK3QTTx/rzV0vVAGOdqsO44w8WW9ufOJHyfVzh+Y4Ms092YnX5k6WJO\n" +
  "lUORsP3E0WMKjEeSk86IZjmCUYt1wjwszLfnoXhHqHD4wNjUosx9YLgrhfg3z/pv\n" +
  "CMRT/2iqu0T6U9w9Q3dGKW94RHaBuNDIC3rYIf9pxhjUEBJRCPQFrx29h+VOqnQM\n" +
  "xibkLhj264+HWwwvVwjmLpIEFHFa8UzxR+AiC7m0De/9AZFXwaIRdwGgTDMyJetk\n" +
  "aV0TH7CLkSh+RoCGYGxmjRMOnn60UMrfWbt5zAUQ61pvaPeojnLMolXI9gmfqZvl\n" +
  "zD+zP6IFAgMBAAECggEAF+M7JF5U4Od4He76acnNlMW5eD9cZdjuqRviTKb3dUB6\n" +
  "n+zakNk344no3L50aa7jnlvl2gP1bQ/X175Thc5Y/+jVKB89yYWFdeM+5gEWoIn9\n" +
  "++PqcerVD6EeMYdz+VkMSSSU8dvnKdU5ZCLDBfq4YECNKO10UjAZ0SEFNQ3BVEgN\n" +
  "p3Kva4LNXxm6IMppVSbKXoesig6bwAIETCjCpJJMtiBgjCvrsWJUk6y7a+q4doYg\n" +
  "Ixfc9OUkwdSd2kUQR82d3sgqBd8hIKAx5gbrzjPblpa2iAguiWGjZZUe/DFJ/+jA\n" +
  "hXkwA7PzvU99GPXmNqFUVenvkaDKGBIDndcv3hcGgQKBgQDII5pUC//u6GnPRW8y\n" +
  "lLCWhid6JtkMeyoNXITngWn1PhWDrS4vPZv0zD1dgE87iwT82Wuou8NcKT2RQ1Sz\n" +
  "oFCvUrQQl0Ne/3rqsC5RjWUBJHkVGTV3rXIkQ/W31iMExACRx4Mxpaqb+EyiwATL\n" +
  "pj6xit8x28G0s/kfLX3KyBFQwQKBgQDDkF66nLmmWkGRXDZkA/7fck2Y0d6kQhmt\n" +
  "DM7Vn1IQfES47AbjvKpEJBwnoqfjA7kxn63ps6iw+NKNlJaJ6nI+c46U1HP/JMWQ\n" +
  "4WmQYDLFcU/gH6hnGbRfOxEl6QJT/pH9j0BLOnLnSIRBiwojXtCjFjp1h2pdxkwg\n" +
  "q0kmuG5eRQKBgQCc31MzN6im8aMmJO2Q/v0fLw44b8THRK7khDVkZIrSAJTKfm7p\n" +
  "9meMzJ348P6p1EDXk/AP9QSe83BfsR/uH+W+VpvvOTFEgAovdGMJ50NZTuZdhEHl\n" +
  "iZ0jEeXC7+TI8nzjxzdtLcZ5tHkVRF895RPVA94IbZD6e+rz5itoS8NOwQKBgDn7\n" +
  "iQqdQzH+Wqhlhb7brGrFshoYR4G+E2zKely+Ew/OOHX3jQdivbzcU/iwckSNqw/6\n" +
  "fw7mdKKb85mEr6mOeHbpviGEaCwC1AzjuG/VBdiigwM5f62p4l/F3Iy37DfJJ7Hd\n" +
  "Ejv7xWurhYMFXWklSIjCI20LTE8nZxTclCDF1/tdAoGBAIMw0hzgjymoqq9oADQn\n" +
  "4DYp69LWHy1aoGfaBBAqNw222VHPt2D8vhFdzse+GwOP3tSPWReg6z8R/CjIunnw\n" +
  "C6WwTrYVOecmJBSYSZ6GWh/I57QDsNPtWKepP3Hs8YtePV4tyzADdFRiCizdfaf+\n" +
  "eW/C93o743kyB+xcHpMq4+ZB\n" +
  "-----END PRIVATE KEY-----";

qz.security.setSignatureAlgorithm("SHA512"); // Since 2.1
qz.security.setSignaturePromise(function (toSign) {
  return function (resolve, reject) {
    try {
      const pk = KEYUTIL.getKey(privateKey);
      const sig = new KJUR.crypto.Signature({ "alg": "SHA512withRSA" });  // Use "SHA1withRSA" for QZ Tray 2.0 and older
      sig.init(pk);
      sig.updateString(toSign);
      const hex = sig.sign();
      resolve(stob64(hextorstr(hex)));
    } catch (err) {
      console.error(err);
      reject(err);
    }
  };
});

async function groupingModifiers (productId, modifiers) {
  const groupedMods = [];
  for (const mod of modifiers) {
    const product = store.getters["config/product"](productId);
    if (!product) {
      const errorMsg = `Could not find product with id ${productId}, skipping`;
      console.error(errorMsg);
      continue;
    }
    const group = product["modifier_groups"].find(sgroup => sgroup.id === mod.group);
    if (!group) {
      const errorMsg = `Could not find modifier group with id ${mod.group.id}, skipping`;
      console.error(errorMsg);
    } else {
      if (!groupedMods.find(groupIter => groupIter.name === group.name)) {
        groupedMods.push({ name: group.name, modifiers: [] });
      }
      groupedMods.find(groupIter => groupIter.name === group.name).modifiers.push(mod);
    }

  }
  return groupedMods;
}

async function buildDateTimeCashierPart(printer, currentEmployee, tableID) {
  // defining localization variables
  const localization = store.getters["config/isLocalization"]
  const localizedFormat = "MM/DD/YYYY"
  const deLocalizedFormat = "DD/MM/YYYY"

  // localizing date format if country is US
  const date = moment(new Date()).format(localization ? localizedFormat : deLocalizedFormat);
  const time = moment(new Date()).format("hh:mm A");
  const tableName = tableID ? (store.getters["config/getTableById"](tableID)).table_name || "unknown" : null;
  printer.tableCustom([
    { text: "Date: " + date, align: "LEFT", width: 1 }
  ]);
  printer.tableCustom([
    { text: "Time: " + time, align: "LEFT", width: 1 }
  ]);
  if (currentEmployee) {
    // Format name as "First Name L." (first name + last name initial)
    const nameParts = currentEmployee.name.split(' ');
    const firstName = nameParts[0];
    const lastNameInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) + '.' : '';
    const formattedName = lastNameInitial ? `${firstName} ${lastNameInitial}` : firstName;

    printer.tableCustom([
      {
        text: "Staff: " + formattedName,
        align: "LEFT",
        width: 1
      }]);
  }
  if (tableName) {
    printer.tableCustom([
      {
        text: "Table: " + tableName,
        align: "LEFT",
        width: 1
      }]);
  }
  return printer;
}

async function buildFooter (printer) {
  printer.tableCustom([
    { text: "Powered by Biyo POS", align: "CENTER", width: 1 }
  ]);
  return printer;
}

async function isDiningOption () {
  const terminal = store.getters["config/terminal"];
  const printer_dining_option = terminal.db_settings.find(setting => setting.name === "printer_dining_option");
  return printer_dining_option ? printer_dining_option.value.toLowerCase() === "true" : true;
}

async function buildTaxesPart (printer, items, isInclusive = false, fallbackTaxTotal = 0) {
  let allTaxes = [];
  const currency = store.getters["config/currency"]
  items.filter(item => !item.void_status).forEach((item) => {
    if (item.tax_items) {
      allTaxes = allTaxes.concat(item.tax_items);
    }
  });
  const byName = groupBy(allTaxes, it => it["name"]);
  const calculatedTax = Object.keys(byName).map(name => {
    const sum = byName[name].reduce((acc, it) => acc + it.tax_total, 0);
    return {
      tax_name: name,
      total: sum
    };
  });

  const itemLevelTotal = calculatedTax.reduce((s, t) => s + t.total, 0);

  if (itemLevelTotal > 0) {
    calculatedTax.forEach((tax) => {
      let taxLabel = "Tax: " + tax.tax_name;
      if (isInclusive) {
        taxLabel = "Includes Tax: " + tax.tax_name;
      }
      printer.tableCustom([
        { text: taxLabel, align: "LEFT", width: 0.50 },
        { text: currency + ' ' + tax.total.toFixed(2), align: "RIGHT", width: 0.45 }]);
    });
  } else if (fallbackTaxTotal > 0) {
    const taxLabel = isInclusive ? "Includes Tax" : "Tax";
    printer.tableCustom([
      { text: taxLabel, align: "LEFT", width: 0.50 },
      { text: currency + ' ' + fallbackTaxTotal.toFixed(2), align: "RIGHT", width: 0.45 }]);
  }
}

/**
 * Resolve the kitchen-ticket name(s) for an order line's product.
 * Normally `kitchen_display_name` overrides the item's own name. When the
 * `kitchen_print_both_names` db_setting is enabled, the kitchen name is kept
 * as primary but the item's normal name is also returned so callers can
 * print it as a second line.
 * @returns {{primaryName: string, secondaryName: string|null}}
 */
function resolveKitchenItemNames(item) {
  let primaryName = item.name;
  let secondaryName = null;
  if (item.product) {
    const product = store.getters["config/product"](item.product);
    if (product && product.kitchen_display_name) {
      primaryName = product.kitchen_display_name;
      const terminalInfo = store.getters["config/terminal"];
      const printBoth = terminalInfo?.db_settings?.find(setting => setting.name === "kitchen_print_both_names")?.value === "true";
      if (printBoth) {
        secondaryName = item.name;
      }
    }
  }
  return { primaryName, secondaryName };
}

async function buildItemsPart (printer, items, kitchen = null, includeVoid = false, printerInfo = null) {
  const terminalInfo = store.getters["config/terminal"];
  const effectivePrinter = printerInfo ?? terminalInfo?.printer;
  const useRedModifiers = !!kitchen && shouldUseRedModifiers(terminalInfo, effectivePrinter);
  const currency = store.getters["config/currency"]
  if (!kitchen) {
    printer
      .tableCustom([
        { text: "Qty", align: "LEFT", width: 0.10 },
        { text: "Item Name", align: "LEFT", width: 0.62 },
        { text: "Subtotal", align: "RIGHT", width: 0.23 }]);
  } else {
    printer
      .tableCustom([
        { text: "Qty", align: "LEFT", width: 0.2 },
        { text: "Item Name", align: "LEFT", width: 0.8 }]);
  }

  printer.line();
  printer.align("ct");

  for (const item of items) {
    if (!item.void_status || includeVoid) {
      // Determine item name to display
      let itemName = item.name;
      let secondaryName = null;

      // If printing for kitchen, check for kitchen_display_name
      if (kitchen && item.product) {
        ({ primaryName: itemName, secondaryName } = resolveKitchenItemNames(item));
      }

      // Truncate product names longer than 25 characters and add period
      let displayName = itemName;
      if (itemName.length > 25) {
        displayName = itemName.substring(0, 24) + '.';
      }

      // Calculate product-only subtotal (base price × quantity, without modifiers)
      let productSubtotal = item.price * Math.abs(item.quantity);

      printer.tableCustom([
        { text: item.quantity, align: "LEFT", width: 0.1 },
        { text: displayName, align: "LEFT", width: kitchen ? 0.8 : 0.62 },
        { text: kitchen ? "" : currency + ' ' + productSubtotal.toFixed(2), align: "RIGHT", width: kitchen ? 0 : 0.23 }
      ]);

      if (secondaryName) {
        let secondaryDisplayName = secondaryName;
        if (secondaryDisplayName.length > 25) {
          secondaryDisplayName = secondaryDisplayName.substring(0, 24) + '.';
        }
        printer.tableCustom([
          { text: "", align: "LEFT", width: 0.1 },
          { text: secondaryDisplayName, align: "LEFT", width: 0.8 }
        ]);
      }

      if (item.modifiers) {
        for (const modifierGroup of await groupingModifiers(item.product, item.modifiers)) {
          // Show individual modifiers with + prefix - gets its own column row
          for (const modifier of modifierGroup.modifiers) {
            if (!modifier.void_status) {
              // Calculate total modifier price (price per unit × quantity)
              const totalModifierPrice = modifier.price * Math.abs(item.quantity);

              if (useRedModifiers) {
                printer.color(1);
                printer.tableCustom([
                  { text: "* " + modifier.name, align: "LEFT", width: 1 }
                ]);
                printer.color(0);
              } else {
                printer.tableCustom([
                  { text: "+", align: "LEFT", width: 0.1 },
                  { text: "Mod. " + modifier.name, align: "LEFT", width: kitchen ? 0.8 : 0.62 },
                  { text: kitchen ? "" : currency + ' ' + totalModifierPrice.toFixed(2), align: "RIGHT", width: kitchen ? 0 : 0.23 }
                ]);
              }
            }
          }
        }
      }

      // Show item-level discount if it exists (after modifiers)
      if (!kitchen && item.discount && item.discount > 0) {
        let discountText = "Discounted";
        // Add percentage if it's a percentage discount
        if (item.discount_type === 1 && item.discount_percentage) {
          discountText = `${item.discount_percentage}% Discounted`;
        }

        // Calculate total discount for the item (discount per unit × quantity)
        const totalItemDiscount = item.discount * Math.abs(item.quantity);

        printer.tableCustom([
          { text: "", align: "LEFT", width: 0.00 },
          { text: discountText + " -" + currency + ' ' + totalItemDiscount.toFixed(2), align: "LEFT", width: .70 },
          { text: "", align: "RIGHT", width: 0.0 }
        ]);
      }

      if (item.notes) {
        printer.tableCustom([
          { text: "", align: "LEFT", width: 0.1 },
          { text: "NOTE: " + item.notes, align: "LEFT", width: kitchen ? 0.8 : 0.62 },
          { text: "", align: "RIGHT", width: kitchen ? 0 : 0.23 }
        ]);
      }
      if (kitchen) {
        printer.line();
      }
    }
  }
  return printer;
}

export async function printReceipt (orderObject, typeOfReceipt = null) {
  // Allow printing for orders with status 0 (PENDING)
  // No need to modify the order status, just proceed with printing
  const terminalInfo = store.getters["config/terminal"];
  const storeInfo = store.getters["config/store"];
  const currentEmployee = store.getters["employee"];
  const printer = new Printer(!!(terminalInfo.printer && terminalInfo.printer["is_58mm"]));
  const currency = store.getters["config/currency"]
  // Get db_settings for signature line, tip line, and tip suggestions
  const dbSettings = terminalInfo["db_settings"] ? JSON.parse(JSON.stringify(terminalInfo["db_settings"])) : [];
  const showSignatureLine = dbSettings.find(setting => setting.name === "receipt_signature_line")?.value === "true";
  const showTipLine = dbSettings.find(setting => setting.name === "receipt_tip_line")?.value === "true";
  const showTipSuggestions = dbSettings.find(setting => setting.name === "receipt_tip_suggest")?.value === "true";
  const showQRCode = dbSettings.find(setting => setting.name === "pt_qrcode")?.value === "true";
  const showCashDiscount = dbSettings.find(setting => setting.name === "cash_discount")?.value === "true";
  // const imagePrint

  // If QR code is enabled, print HTML receipt only and return early
  if (showQRCode) {
    try {
      await printHTMLReceipt(orderObject, typeOfReceipt, true, null);
      return;
    } catch (error) {
      console.error("Failed to print HTML receipt:", error);
      throw error;
    }
  }

  printer.align("ct");
  printer.tableCustom([
    { text: storeInfo.name, align: "CENTER", width: defaultWidthPercent }
  ]);
  if (storeInfo.address) {
    printer.tableCustom([
      { text: storeInfo.address, align: "CENTER", width: defaultWidthPercent }
    ]);
  }
  if (storeInfo.address_2) {
    printer.tableCustom([
      { text: storeInfo.address_2, align: "CENTER", width: defaultWidthPercent }
    ]);
  }
  printer.tableCustom([
    { text: "Phone: " + storeInfo.phone, align: "CENTER", width: defaultWidthPercent }
  ]);
  if (storeInfo.fax) {
    printer.tableCustom([
      { text: "Fax: " + storeInfo.fax, align: "CENTER", width: defaultWidthPercent }
    ]);
  }
  if (storeInfo.email) {
    printer.tableCustom([
      { text: storeInfo.email, align: "CENTER", width: defaultWidthPercent }
    ]);
  }
  if (storeInfo.website) {
    printer.tableCustom([
      { text: storeInfo.website, align: "CENTER", width: defaultWidthPercent }
    ]);
  }
  if (typeOfReceipt === "refund") {
    printer.line();
    printer.tableCustom([
      { text: "*** REFUND RECEIPT ***", align: "CENTER", width: 1 }
    ]);
    printer.line();
  }
  printer.text("");
  if (storeInfo.receipt_header_text) {
    printer.line();
    printer.align("ct");
    // printer.text(storeInfo.receipt_header_text);
    printer.tableCustom([
      { text: storeInfo.receipt_header_text, align: "CENTER", width: 1 }
    ]);
    printer.line();
    printer.align("lt");
  }

  printer.align("ct");
  printer.tableCustom([
    {
      text: `Sales Receipt#: ${orderObject.id}`,
      align: "LEFT",
      width: 1
    }]);
  if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    printer.tableCustom([
      {
        text: `Dining Option: ${terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name}`,
        align: "LEFT",
        width: 1
      }]);
  }

  await buildDateTimeCashierPart(printer, currentEmployee, orderObject.table);
  printer.text("");
  printer.align("ct");
  await buildItemsPart(printer, orderObject.items);

  // Add separator line before totals section
  printer.line();

  // Calculate pre-discount subtotal for the entire order
  let preDiscountOrderSubtotal = 0;
  for (const item of orderObject.items) {
    if (!item.void_status) {
      // Add base item price × quantity
      preDiscountOrderSubtotal += item.price * Math.abs(item.quantity);

      // Add modifiers × quantity
      if (item.modifiers) {
        for (const modifier of item.modifiers) {
          if (!modifier.void_status) {
            preDiscountOrderSubtotal += modifier.price * Math.abs(item.quantity);
          }
        }
      }
    }
  }

  // subtotal
  printer.align("ct");
  printer.tableCustom([
    { text: "Subtotal:", align: "LEFT", width: 0.50 },
    { text: currency + ' ' + preDiscountOrderSubtotal.toFixed(2), align: "RIGHT", width: 0.45 }]);

  // Only show order discount if there is one
  if (orderObject.discount_total && orderObject.discount_total > 0) {
    printer.tableCustom([
      { text: "Order Discount:", align: "LEFT", width: 0.50 },
      { text: "-" + currency + ' ' +orderObject.discount_total.toFixed(2), align: "RIGHT", width: 0.45 }]);
  }

    const isTaxInclusive = store.getters["config/isTaxInclusive"];
    await buildTaxesPart(printer, orderObject.items, isTaxInclusive, orderObject.tax_total || 0);

  if(orderObject.surcharge_value && orderObject.surcharge_value > 0 ) {
    printer.tableCustom([
      { text: "Non-Cash Fee:", align: "LEFT", width: 0.50 },
      { text: currency + ' ' +orderObject.surcharge_value.toFixed(2), align: "RIGHT", width: 0.45 }
    ]);
  }

  if (orderObject.tips_total && orderObject.tips_total > 0) {
      printer.tableCustom([
          { text: "Tip:", align: "LEFT", width: 0.50 },
          { text: currency + ' ' + orderObject.tips_total.toFixed(2), align: "RIGHT", width: 0.45 }
      ]);
  }

  // Add separator line before total
  printer.line();
  printer.align("ct");

  const finalTotal = getOrderFinalTotal(orderObject);
  if (showCashDiscount) {
    const CASH_DISCOUNT_FACTOR = getCashDiscountFactor(dbSettings);
    const nonCashTotal = orderObject.cash_discount ? finalTotal * CASH_DISCOUNT_FACTOR : finalTotal;
    const cashTotal = orderObject.cash_discount ? finalTotal : finalTotal / CASH_DISCOUNT_FACTOR;
    printer.tableCustom([
      { text: "Total (Non-Cash):", align: "LEFT", width: 0.50 },
      { text: currency + ' ' + nonCashTotal.toFixed(2), align: "RIGHT", width: 0.45 }]);
    printer.tableCustom([
      { text: "Total (Cash):", align: "LEFT", width: 0.50 },
      { text: currency + ' ' + cashTotal.toFixed(2), align: "RIGHT", width: 0.45 }]);
  } else {
    printer.tableCustom([
      { text: "TOTAL:", align: "LEFT", width: 0.50 },
      { text: currency + ' ' + finalTotal.toFixed(2), align: "RIGHT", width: 0.45 }]);
  }
  if (orderObject.payments.length > 0) {
    orderObject.payments.forEach(function (payment) {
      // Add a separator line before payment type
      printer.line("-");

      // Set alignment to center for payment section
      printer.align("ct");
      printer.tableCustom([
        { text: "Tendered:", align: "LEFT", width: 0.30 },
        { text: orderObject.surcharge_value ? currency +' '+ (payment.amount + orderObject.surcharge_value).toFixed(2) : currency +' '+ payment.amount.toFixed(2), align: "RIGHT", width: 0.65 }]
      );
      printer.tableCustom([
        { text: "Change:", align: "LEFT", width: 0.30 },
        { text: currency +' '+payment.change_amount.toFixed(2), align: "RIGHT", width: 0.65 }]
      );
      printer.tableCustom([
        { text: "Payment Type:", align: "LEFT", width: 0.35 },
        { text: payment.payment_type, align: "RIGHT", width: 0.60 }]
      );

      // Try to extract Dejavoo Online (iPOSpays) iposhpresponse from processor_response
      let iposhpData = null;
      if (payment.processor_response && payment.processor_response !== 'no data') {
        try {
          const _parsed = JSON.parse(payment.processor_response);
          iposhpData = _parsed.iposhpresponse || _parsed.iposTransactResponse || null;
        } catch (e) {
          try {
            // Legacy backend stored dicts as Python repr (single quotes)
            const _fixed = payment.processor_response
              .replace(/'/g, '"').replace(/\bNone\b/g, 'null')
              .replace(/\bTrue\b/g, 'true').replace(/\bFalse\b/g, 'false');
            const _parsed2 = JSON.parse(_fixed);
            iposhpData = _parsed2.iposhpresponse || _parsed2.iposTransactResponse || null;
          } catch (e2) { /* non-critical */ }
        }
      }

      // Display credit card details if available (for Dejavoo or NABancard)
      if (payment.card_lastfour) {
        printer.tableCustom([
          { text: "Card Number:", align: "LEFT", width: 0.30 },
          { text: `XXXX-XXXX-XXXX-${payment.card_lastfour}`, align: "RIGHT", width: 0.65 }]
        );
      }

      // Check if this is a credit card payment
      const isCreditCardPayment = (payment.payment_type &&
        (payment.payment_type.toLowerCase().includes('credit') ||
         payment.payment_type.toLowerCase().includes('card') ||
         payment.payment_type.toLowerCase().includes('dejavoo') ||
         payment.payment_type.toLowerCase().includes('nabancard'))) ||
        !!iposhpData;


      // If this is a credit card payment, try to extract details from processor_response
      if (isCreditCardPayment && payment.processor_response) {
        try {
          // Try to parse the processor response
          const processorData = JSON.parse(payment.processor_response);

          // Extract card details from processor response if they're not already in the payment object
          if (processorData.CardData) {
            // Display card type
            if (processorData.CardData.CardType && !payment.card_type) {
              printer.tableCustom([
                { text: "Card Type:", align: "LEFT", width: 0.30 },
                { text: processorData.CardData.CardType, align: "RIGHT", width: 0.65 }]
              );
            }

            // Display entry method
            if (processorData.CardData.EntryType && !payment.entry_type) {
              printer.tableCustom([
                { text: "Entry Method:", align: "LEFT", width: 0.35 },
                { text: processorData.CardData.EntryType, align: "RIGHT", width: 0.60 }]
              );
            }
          }

          // Extract transaction details
          if (processorData.AuthCode && !payment.auth_code) {
            printer.tableCustom([
              { text: "Auth Code:", align: "LEFT", width: 0.30 },
              { text: processorData.AuthCode, align: "RIGHT", width: 0.65 }]
            );
          }

          if (processorData.TransactionType && !payment.dejavoo_transaction_type) {
            printer.tableCustom([
              { text: "Transaction:", align: "LEFT", width: 0.35 },
              { text: processorData.TransactionType, align: "RIGHT", width: 0.60 }]
            );
          }

          if (processorData.GeneralResponse && processorData.GeneralResponse.DetailedMessage && !payment.approval_message) {
            printer.tableCustom([
              { text: "Approval:", align: "LEFT", width: 0.35 },
              { text: processorData.GeneralResponse.DetailedMessage, align: "RIGHT", width: 0.60 }]
            );
          }

          if (processorData.CardData && processorData.CardData.Name && !payment.cardholder_name) {
            printer.tableCustom([
              { text: "Cardholder:", align: "LEFT", width: 0.35 },
              { text: processorData.CardData.Name, align: "RIGHT", width: 0.60 }]
            );
          }
        } catch (e) { /* non-critical print field */ }
      }

      // Display additional Dejavoo payment details if available
      if (payment.card_type) {
        printer.tableCustom([
          { text: "Card Type:", align: "LEFT", width: 0.30 },
          { text: payment.card_type, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.entry_type) {
        printer.tableCustom([
          { text: "Entry Method:", align: "LEFT", width: 0.30 },
          { text: payment.entry_type, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.auth_code) {
        printer.tableCustom([
          { text: "Auth Code:", align: "LEFT", width: 0.30 },
          { text: payment.auth_code, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.dejavoo_transaction_type) {
        printer.tableCustom([
          { text: "Transaction Type:", align: "LEFT", width: 0.30 },
          { text: payment.dejavoo_transaction_type, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.approval_message) {
        printer.tableCustom([
          { text: "Approval:", align: "LEFT", width: 0.30 },
          { text: payment.approval_message, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.reference_id) {
        printer.tableCustom([
          { text: "Reference ID:", align: "LEFT", width: 0.30 },
          { text: payment.reference_id, align: "RIGHT", width: 0.65 }]
        );
      }

      if (payment.cardholder_name) {
        printer.tableCustom([
          { text: "Cardholder:", align: "LEFT", width: 0.30 },
          { text: payment.cardholder_name, align: "RIGHT", width: 0.65 }]
        );
      }

      // Display Dejavoo Online (iPOSpays) payment details from iposhpresponse
      if (iposhpData) {
        const cardLabel = [iposhpData.label, iposhpData.cardType].filter(Boolean).join(' ');
        if (cardLabel) {
          printer.tableCustom([
            { text: "Card Type:", align: "LEFT", width: 0.30 },
            { text: cardLabel, align: "RIGHT", width: 0.65 }]
          );
        }
        if (iposhpData.maskedPan) {
          const lastFour = String(iposhpData.maskedPan).slice(-4);
          printer.tableCustom([
            { text: "Card Number:", align: "LEFT", width: 0.30 },
            { text: `XXXX-XXXX-XXXX-${lastFour}`, align: "RIGHT", width: 0.65 }]
          );
        }
        if (iposhpData.responseApprovalCode) {
          printer.tableCustom([
            { text: "Auth Code:", align: "LEFT", width: 0.30 },
            { text: String(iposhpData.responseApprovalCode), align: "RIGHT", width: 0.65 }]
          );
        }
        if (iposhpData.transactionReferenceId) {
          printer.tableCustom([
            { text: "Reference ID:", align: "LEFT", width: 0.30 },
            { text: String(iposhpData.transactionReferenceId), align: "RIGHT", width: 0.65 }]
          );
        }
        if (iposhpData.rrn) {
          printer.tableCustom([
            { text: "RRN:", align: "LEFT", width: 0.30 },
            { text: String(iposhpData.rrn), align: "RIGHT", width: 0.65 }]
          );
        }
        if (iposhpData.hostResponseMessage) {
          printer.tableCustom([
            { text: "Approval:", align: "LEFT", width: 0.30 },
            { text: String(iposhpData.hostResponseMessage).trim(), align: "RIGHT", width: 0.65 }]
          );
        }
      }

      // Add tip line for credit card payments if enabled in settings
      if (showTipLine && isCreditCardPayment) {
        printer.text("");
        printer.tableCustom([
          { text: "TIP:", align: "LEFT", width: 0.35 },
          { text: "____________", align: "RIGHT", width: 0.60 }]
        );
        printer.text("");
        printer.tableCustom([
          { text: "TOTAL:", align: "LEFT", width: 0.35 },
          { text: "____________", align: "RIGHT", width: 0.60 }]
        );
        printer.text("");
      }

      // Add signature line for credit card payments if enabled in settings
      if (showSignatureLine && isCreditCardPayment) {
        printer.text("");
        printer.tableCustom([
          { text: "X", align: "LEFT", width: 1.0 }]
        );
        printer.line("");
        printer.align("ct");
        printer.text("SIGNATURE");
        printer.text("");
        printer.align("ct");
        printer.text("I agree to pay the above amount");
        printer.text("according to the card issuer agreement.");

        printer.text("");
      }

      // Add tip suggestions table for credit card payments if enabled in settings
      if (showTipSuggestions && isCreditCardPayment) {
        printer.text("");
        printer.align("ct");

        // Calculate tip suggestions based on payment amount
        // Use the actual payment amount (accounting for surcharge if present)
        const baseAmount = orderObject.surcharge_value ?
          parseFloat(payment.amount) + parseFloat(orderObject.surcharge_value) :
          parseFloat(payment.amount);

        // Only show tip suggestions if we have a valid amount
        if (baseAmount > 0) {
          // Tip percentages
          const tipPercentages = [15, 18, 20];

          // Header row
          printer.tableCustom([
            { text: "Tip", align: "LEFT", width: 0.30 },
            { text: "Amount", align: "CENTER", width: 0.35 },
            { text: "Total", align: "RIGHT", width: 0.30 }
          ]);

          // Print tip suggestions
          tipPercentages.forEach(percentage => {
            const tipAmount = Math.round(baseAmount * (percentage / 100) * 100) / 100;
            const totalWithTip = baseAmount + tipAmount;

            printer.tableCustom([
              { text: `${percentage}%`, align: "LEFT", width: 0.30 },
              { text: `${currency} ${tipAmount.toFixed(2)}`, align: "CENTER", width: 0.35 },
              { text: `${currency} ${totalWithTip.toFixed(2)}`, align: "RIGHT", width: 0.30 }
            ]);
          });

          printer.text("");
        }
      }

    });
  }

  if (orderObject["customer"]) {
    printer.line();
    printer.align("ct");
    printer.title("CUSTOMER INFORMATION");
    printer.line();
    printer.align("ct");
    if (orderObject["customer"].first_name) printer.tableCustom([
      { text: `First Name: ${orderObject["customer"].first_name}`, align: "LEFT", width: 1 }]);
    if (orderObject["customer"].last_name) printer.tableCustom([
      { text: `Last Name: ${orderObject["customer"].last_name}`, align: "LEFT", width: 1 }]);
    if (orderObject["customer"].phone) printer.tableCustom([
      { text: `Phone: ${orderObject["customer"].phone}`, align: "LEFT", width: 1 }]);

    // adding customer address on receipts
    let defaultAddress = null;

    // First try to get default_address property (for backward compatibility)
    if(orderObject["customer"].default_address) {
      defaultAddress = orderObject["customer"].default_address;
    }
    // If not found, look for default address in addresses array
    else if(orderObject["customer"].addresses && orderObject["customer"].addresses.length > 0) {
      defaultAddress = orderObject["customer"].addresses.find(address => address.default === true);
      // If no default found, use the first address
      if(!defaultAddress) {
        defaultAddress = orderObject["customer"].addresses[0];
      }
    }

    if(defaultAddress) {
      printer.tableCustom([
        { text: `Address: ${defaultAddress?.address_1}`, align: "LEFT", width: 1 }]);
      if(defaultAddress?.address_2)
        printer.tableCustom([
          { text: `Appartment, Suite: ${defaultAddress?.address_2}`, align: "LEFT", width: 1 }]);
      if(defaultAddress?.country)
        printer.tableCustom([
          { text: `Country: ${defaultAddress?.country}`, align: "LEFT", width: 1 }]);
      if(defaultAddress?.state)
        printer.tableCustom([
          { text: `State: ${defaultAddress?.state}`, align: "LEFT", width: 1 }]);
      if(defaultAddress?.city)
        printer.tableCustom([
          { text: `City: ${defaultAddress?.city}`, align: "LEFT", width: 1 }]);
      if(defaultAddress?.postal_code)
        printer.tableCustom([
          { text: `Postal Code: ${defaultAddress?.postal_code}`, align: "LEFT", width: 1 }]);
    }

  }
  if (storeInfo.receipt_footer_text) {
    printer.line();
    printer.align("ct");
    printer.tableCustom([
      { text: storeInfo.receipt_footer_text, align: "CENTER", width: 1 }
    ]);
    printer.line();
  }

  // Normal ESC/POS receipt
  printer.align("ct");
  printer.barcode(orderObject.id, "CODE39", {});
  printer.text("");
  await buildFooter(printer);
  await printer.print();
}

function getPerPrinterObjects (orderObject, kitchenItems) {
  const kitchenObjects = [];
  kitchenItems.forEach(function (item) {
    item.printers.forEach(function (printer) {
      if (item.has_kitchen_updates || printer.is_kds) {
        if (printer.print_per_single_item) {
          // Create separate print object for each quantity
          for (let i = 0; i < item.quantity; i++) {
            // __kitchenUnitIndex: which unit of this item's original quantity
            // this split-out ticket represents (1-based). Lets a per-ticket
            // template (e.g. Design 4) number tickets "1 of N", "2 of N", ...
            // across an order even though each unit prints as its own,
            // independent call — see printKitchenReceiptTemplate4.
            const singleItem = { ...item, quantity: 1, __kitchenUnitIndex: i + 1 };
            kitchenObjects.push({
              printerInfo: printer,
              items: [singleItem]
            });
          }
        } else {
          // Group items by printer
          if (!kitchenObjects.find(printerObj => printerObj.printerInfo.id === printer.id)) {
            kitchenObjects.push({ printerInfo: printer, items: [] });
          }
          kitchenObjects.find(printerObj => printerObj.printerInfo.id === printer.id).items.push(item);
        }
      }
    });
  });
  return kitchenObjects;
}

/**
 * Sends order cancellation to all KDS printers
 * @param {Object} orderObject - The order being canceled
 * @returns {Promise<Object>} - Results of the cancellation
 */
export async function sendOrderCancellationToAllKds(orderObject) {

  // Only proceed if the order was previously sent to the kitchen
  if (!orderObject.receipt_kitchen_printed) {
    return { errors: [] };
  }

  const results = {
    errors: []
  };

  // Get all KDS printers
  const kdsPrinters = store.getters["config/printers"].filter(printer => printer.is_kds);

  // Create order general info with items
  const orderGeneralInfo = {
    id: orderObject.id,
    local_id: orderObject.local_id,
    related_terminal: orderObject.related_terminal,
    open_date: orderObject.open_date,
    updated_at: orderObject.updated_at,
    table: orderObject.table || orderObject.table_id,
    dining_option: orderObject.dining_option,
    dining_option_type: orderObject.dining_option_type,
    customer: orderObject.customer,
    items: orderObject.items // Include all items for cancellation
  };

  // Send cancellation to each KDS printer
  const promises = kdsPrinters.map(async (printer) => {
    try {
      await sendOrderCancellationToKds(orderGeneralInfo, printer);
    } catch (e) {
      console.error("Cannot send order cancellation to KDS:", printer, e);
      results.errors.push({
        error: e,
        printer: printer
      });
    }
  });

  await Promise.all(promises);

  return results;
}

export async function printKitchenReceipts (orderObject, kitchenItems, reason = undefined) {
  const printerObjects = getPerPrinterObjects(orderObject, kitchenItems);
  const printResults = {
    errors: []
  };

  // Use Promise.all to handle all printers in parallel
  // This is crucial for KDS to avoid race conditions where the first KDS marks items as sent
  // before the second KDS processes them
  const promises = printerObjects.map(async (obj) => {
    try {
      if (!obj.printerInfo.is_kds) {
        await printKitchenReceipt(orderObject, obj, reason);
      } else {
        const orderGeneralInfo = {
          id: orderObject.id,
          local_id: orderObject.local_id,
          related_terminal: orderObject.related_terminal,
          open_date: orderObject.open_date,
          updated_at: orderObject.updated_at,
          table: orderObject.table || orderObject.table_id,
          dining_option: orderObject.dining_option,
          dining_option_type: orderObject.dining_option_type,
          customer: orderObject.customer,
          items: orderObject.items // Include items for KDS cancellation
        };
        await sendToKds(obj, obj.printerInfo, orderGeneralInfo, orderObject.receipt_kitchen_printed, reason);
      }
    } catch (e) {
      console.error("Can not print to ", obj.printerInfo);
      printResults.errors.push({
          error: e,
          objectToPrint: obj
        }
      );
    }
  });

  await Promise.all(promises);
  return printResults;
}

// Style 1: Original design (default)
async function generateZPLStyle1(kitchenObj, orderObject, terminalInfo, currentEmployee) {
  const data = [];

  // Set initial position and font
  let yPosition = 50;
  const xPosition = 50;
  const lineHeight = 30;

  // Add dining option if available
  if (await isDiningOption() && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    const diningOption = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type);
    data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FD${diningOption.name}^FS`);
    yPosition += lineHeight;
  }

  // Add separator line
  data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`);
  yPosition += lineHeight;

  // Add customer information if available
  if ((orderObject.customer && (orderObject.customer.first_name || orderObject.customer.last_name)) || (orderObject.customer && orderObject.customer.phone)) {
    const customerName = orderObject.customer ?
      `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim() : '';
    const customerPhone = orderObject.customer ? orderObject.customer.phone || '' : '';

    if (customerName) {
      data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FD${customerName}^FS`);
      yPosition += lineHeight;
    }

    if (customerPhone) {
      data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FD${customerPhone}^FS`);
      yPosition += lineHeight;
    }

    if (customerName || customerPhone) {
      data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`);
      yPosition += lineHeight;
    }
  }

  // Order info section
  data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDOrder# ${orderObject.id}^FS`);
  yPosition += lineHeight;

  data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDDate: ${moment().format('MM/DD/YYYY')}^FS`);
  yPosition += lineHeight;

  data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDTime: ${moment().format('hh:mm A')}^FS`);
  yPosition += lineHeight;

  // Add separator line
  data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`);
  yPosition += lineHeight;

  // Items section
  for (const item of kitchenObj.items) {
    // Determine item name (use kitchen_display_name if available)
    const { primaryName: itemName, secondaryName } = resolveKitchenItemNames(item);

    // Item name with quantity
    data.push(`^FO${xPosition},${yPosition}^A0N,30,30^FD${item.quantity}x ${itemName}^FS`);
    yPosition += lineHeight;

    if (secondaryName) {
      data.push(`^FO${xPosition},${yPosition}^A0N,22,22^FD${secondaryName}^FS`);
      yPosition += lineHeight;
    }

    data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`); // Separator after item name
    yPosition += lineHeight;

    // Add modifiers if any
    if (item.modifiers && item.modifiers.length) {
      const groupedMods = await groupingModifiers(item.product, item.modifiers);
      for (const group of groupedMods) {
        // Add modifier group name
        data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FD${group.name}:^FS`);
        yPosition += lineHeight;

        // Add modifiers in this group
        for (const mod of group.modifiers) {
          if (!mod.void_status) {
            data.push(`^FO${xPosition + 20},${yPosition}^A0N,25,25^FD-> ${mod.name}^FS`);
            yPosition += lineHeight;
          }
        }
      }

      data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`); // Separator after modifiers
      yPosition += lineHeight;
    }

    // Add item notes if exists
    if (item.notes) {
      data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDNotes:^FS`);
      yPosition += lineHeight;

      data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FD${item.notes}^FS`);
      yPosition += lineHeight;

      data.push(`^FO${xPosition},${yPosition}^GB350,3,3^FS`); // Separator after notes
      yPosition += lineHeight;
    }

    yPosition += 10; // Extra space between items
  }

  // Footer section
  data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDCashier: ${currentEmployee ? currentEmployee.name : ''}^FS`);
  yPosition += lineHeight;

  data.push(`^FO${xPosition},${yPosition}^A0N,25,25^FDTerminal: ${terminalInfo.name || 'Biyo POS'}^FS`);
  yPosition += lineHeight;

  return data;
}

// Style 2: Compact design for 2" x 1" labels (HTML-inspired layout)
async function generateZPLStyle2(kitchenObj, orderObject, terminalInfo) {
  const data = [];

  // Label dimensions for 2" x 1" label at 203 DPI
  const labelWidth = 403; // 2 inches = 203 dots
  let yPosition = 30;
  const xPadding = 50; // Left padding from edge (6px equivalent)

  // Process each item - each gets its own label
  for (const item of kitchenObj.items) {
    // Start new label
    data.push('^XA');

    yPosition = 10; // Reset Y position for each label

    // TOP ROW: Order number (left, bold, larger) and Dining option/Order type (right, bordered)
    // Using same left padding as rest of content
    const orderNum = `#${orderObject.id}`;

    // Order number (left, bold, larger font) - use left padding
    data.push(`^FO${xPadding},${yPosition}^A0N,32,32^FD${orderNum}^FS`);

    // Dining option/Order type (right, with border box effect) - align to right edge
    let diningOptionName = '';
    if (await isDiningOption() && orderObject.dining_option_type && terminalInfo["dining_options"]) {
      const diningOption = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type);
      if (diningOption) {
        diningOptionName = diningOption.name;
      }
    }

    if (diningOptionName) {
      // Draw border box for dining option (simulate border with graphics)
      const boxWidth = 40;
      const boxHeight = 18;
      const boxX = labelWidth - boxWidth; // Right edge, absolute position
      const boxY = yPosition;
      // Draw box border
      data.push(`^FO${boxX},${boxY}^GB${boxWidth},${boxHeight},2^FS`);
      // Text inside box
      data.push(`^FO${boxX + 2},${boxY + 4}^A0N,18,18^FD${diningOptionName}^FS`);
    }

    yPosition += 22;

    // META LINE: "For: [name]" (left) and "Time: [time]" (right) - using left padding
    const customerName = orderObject.customer ?
      `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim() : '';
    const orderTime = moment().format('hh:mm A');

    if (customerName) {
      const forText = `For: ${customerName}`;
      data.push(`^FO${xPadding},${yPosition}^A0N,18,18^FD${forText}^FS`);
    }

    // Time (right-aligned) - use field block with padding
    const timeText = `Time: ${orderTime}`;
    const availableWidth = labelWidth - xPadding;
    data.push(`^FO${xPadding},${yPosition}^A0N,18,18^FB${availableWidth},1,0,R^FD${timeText}^FS`);

    yPosition += 18;

    // DIVIDER (solid line - full width)
    data.push(`^FO0,${yPosition}^GB${labelWidth},1,1^FS`);
    yPosition += 6;

    // ITEM SECTION
    // Quantity and name on one line
    const productName = `${item.quantity}x ${item.name}`;
    data.push(`^FO${xPadding},${yPosition}^A0N,20,20^FD${productName}^FS`);
    yPosition += 20;

    // Modifiers - each group on its own line, items within group separated by &
    if (item.modifiers && item.modifiers.length) {
      const groupedMods = await groupingModifiers(item.product, item.modifiers);

      for (const group of groupedMods) {
        const groupModifiers = [];
        for (const mod of group.modifiers) {
          if (!mod.void_status) {
            groupModifiers.push(mod.name);
          }
        }

        if (groupModifiers.length > 0) {
          // Format: "Group Name: item1 & item2 & item3" - each group on its own line
          // Use field block for text wrapping on overflow
          const groupText = `${group.name}: ${groupModifiers.join(' & ')}`;
          const availableWidth = labelWidth - xPadding;
          // ^FB: width, max_lines, space_between_lines, justification (L=left)
          data.push(`^FO${xPadding},${yPosition}^A0N,16,16^FB${availableWidth},3,0,L^FD${groupText}^FS`);
          // Add extra space to account for potential wrapping (3 lines max)
          yPosition += 20;
        }
      }
    }

    // End of label
    data.push('^XZ');
  }

  return data;
}

// Style 3: NEW Design for 2" x 2.25" labels - Optimized vertical layout
async function generateZPLStyle3(kitchenObj, orderObject) {
  const data = [];

  // Label dimensions for 2" x 2.25" label at 203 DPI (same width as Design 2, taller height)
  const labelWidth = 403; // 2 inches = 403 dots (same as Design 2)
  let yPosition = 20;
  const xPadding = 20; // Left padding from edge

  // Process each item - each gets its own label
  for (const item of kitchenObj.items) {
    // Start new label
    data.push('^XA');

    yPosition = 20; // Reset Y position for each label

    // ===== HEADER SECTION =====
    // Order number (left), Customer name (middle), Time (right) - ALL ON SAME ROW
    const orderNum = `#${orderObject.id}`;
    const customerName = orderObject.customer ?
      `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim() : '';
    const orderTime = moment().format('hh:mm A');

    // Order number on left
    data.push(`^FO${xPadding},${yPosition}^A0N,35,35^FD${orderNum}^FS`);

    // Customer name in middle (if available)
    if (customerName) {
      const middleX = 150; // Approximate middle position
      data.push(`^FO${middleX},${yPosition}^A0N,22,22^FD${customerName}^FS`);
    }

    // Time on right
    const availableWidth = labelWidth - xPadding; // Width for time field block (403 - 20 = 383)
    data.push(`^FO${xPadding},${yPosition}^A0N,22,22^FB${availableWidth},1,0,R^FD${orderTime}^FS`);

    yPosition += 35;

    // Divider after header
    data.push(`^FO0,${yPosition}^GB${labelWidth},2,2^FS`);
    yPosition += 12;

    // ===== ITEM SECTION =====
    // Product name with quantity - smaller font
    const productName = `${item.quantity}x ${item.name}`;
    data.push(`^FO${xPadding},${yPosition}^A0N,22,22^FD${productName}^FS`);
    yPosition += 28;

    // ===== MODIFIERS SECTION =====
    if (item.modifiers && item.modifiers.length) {
      // Light divider before modifiers
      data.push(`^FO${xPadding},${yPosition}^GB${labelWidth - (xPadding * 2)},1,1^FS`);
      yPosition += 8;

      const groupedMods = await groupingModifiers(item.product, item.modifiers);

      for (const group of groupedMods) {
        const groupModifiers = [];
        for (const mod of group.modifiers) {
          if (!mod.void_status) {
            groupModifiers.push(mod.name);
          }
        }

        if (groupModifiers.length > 0) {
          if (groupModifiers.length === 1) {
            // If only 1 modifier, put it on the same line as the group name
            const singleLineText = `${group.name}: ${groupModifiers[0]}`;
            data.push(`^FO${xPadding},${yPosition}^A0N,20,20^FD${singleLineText}^FS`);
            yPosition += 26;
          } else {
            // If more than 1 modifier, display group name on one line, then each modifier separately
            // Group name - SMALLER font (20pt instead of 24pt)
            data.push(`^FO${xPadding},${yPosition}^A0N,20,20^FD${group.name}:^FS`);
            yPosition += 20;

            // Individual modifiers indented
            for (const modName of groupModifiers) {
              const availableWidth = labelWidth - (xPadding + 40);
              data.push(`^FO${xPadding + 20},${yPosition}^A0N,22,22^FB${availableWidth},2,0,L^FD+ ${modName}^FS`);
              yPosition += 26;
            }
          }

          yPosition += 4; // Extra space between groups
        }
      }
    }

    // ===== NOTES SECTION =====
    if (item.notes) {
      yPosition += 8;
      // Divider before notes
      data.push(`^FO${xPadding},${yPosition}^GB${labelWidth - (xPadding * 2)},1,1^FS`);
      yPosition += 8;

      data.push(`^FO${xPadding},${yPosition}^A0N,24,24^FDNote:^FS`);
      yPosition += 28;

      const availableWidth = labelWidth - (xPadding + 20);
      data.push(`^FO${xPadding + 20},${yPosition}^A0N,22,22^FB${availableWidth},4,0,L^FD${item.notes}^FS`);
    }

    // End of label
    data.push('^XZ');
  }

  return data;
}

async function printKitchenLabelESCP(kitchenObj, orderObject, reason) {
  Vue.$log.warn(`Print kitchen label ${reason ? "(" + reason + "}" : "(no reason)"} to `, kitchenObj.printerInfo);

  const terminalInfo = store.getters["config/terminal"];
  const currentEmployee = store.getters.employee;
  const separator = '-'.repeat(20);

  // Check if zebra_label setting is enabled
  const zebraLabelSetting = terminalInfo.db_settings.find(setting => setting.name === "zebra_label");
  const useZebraLabel = zebraLabelSetting ? zebraLabelSetting.value.toLowerCase() === "true" : false;

  // Check for zebra_label_style setting
  const zebraLabelStyleSetting = terminalInfo.db_settings.find(setting => setting.name === "zebra_label_style");
  const zebraLabelStyle = zebraLabelStyleSetting ? zebraLabelStyleSetting.value : "1"; // Default to style 1

  let data = [];

  if (useZebraLabel) {
    if (zebraLabelStyle === "2") {
      // Style 2: Compact design for 2" x 1" labels (each item gets its own label with ^XA/^XZ)
      const style2Data = await generateZPLStyle2(kitchenObj, orderObject, terminalInfo);
      data = data.concat(style2Data);
    } else if (zebraLabelStyle === "3") {
      // Style 3: Design for 2" x 2.25" labels (each item gets its own label with ^XA/^XZ)
      const style3Data = await generateZPLStyle3(kitchenObj, orderObject);
      data = data.concat(style3Data);
    } else {
      // Style 1: Original design (default) - single label with outer ^XA/^XZ
      data.push('^XA'); // Start ZPL format
      const style1Data = await generateZPLStyle1(kitchenObj, orderObject, terminalInfo, currentEmployee);
      data = data.concat(style1Data);
      data.push('^XZ'); // End ZPL format
    }
  } else {
    // Original ESC/P code for non-Zebra printers
    // Initialize ESC/P commands with portrait mode
    data = [
      '\x1B\x69\x61\x00\x1B\x40', // set printer to ESC/P mode and clear memory buffer
      '\x1B\x69\x4C\x00', // set portrait mode (0 for portrait, 1 for landscape)
      '\x1B\x55\x02\x1B\x33\x0F', // set margin and line feed values
      '\x1B\x6B\x0B\x1B\x58\x00\x3A\x00', // set font and font size
    ];

    // Add dining option if available
    if (await isDiningOption() && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
      const diningOption = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type);
      // Add spaces before and after dining option name for centering
      const padding = Math.floor((20 - diningOption.name.length) / 2);
      data.push(' '.repeat(padding) + diningOption.name);
      data.push('\x0A');
    }

    // First separator
    data.push(separator);
    data.push('\x0A');

    // Add customer information if available
    if ((orderObject.customer && (orderObject.customer.first_name || orderObject.customer.last_name)) || (orderObject.customer && orderObject.customer.phone)) {
      const customerName = orderObject.customer ?
        `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim() : '';
      const customerPhone = orderObject.customer ? orderObject.customer.phone || '' : '';

      if (customerName) {
        const padding = Math.floor((20 - customerName.length) / 2);
        data.push(' '.repeat(padding) + customerName);
        data.push('\x0A');
      }

      if (customerPhone) {
        const padding = Math.floor((20 - customerPhone.length) / 2);
        data.push(' '.repeat(padding) + customerPhone);
        data.push('\x0A');
      }

      if (customerName || customerPhone) {
        data.push(separator);
        data.push('\x0A');
      }
    }

    // Order info section
    data.push(`Order# ${orderObject.id}`);
    data.push('\x0A');
    data.push(`Date: ${moment().format('MM/DD/YYYY')}`);
    data.push('\x0A');
    data.push(`Time: ${moment().format('hh:mm A')}`);
    data.push('\x0A');

    // Second separator
    data.push(separator);
    data.push('\x0A');

    // Items section
    for (const item of kitchenObj.items) {
      // Determine item name (use kitchen_display_name if available)
      const { primaryName: itemName, secondaryName } = resolveKitchenItemNames(item);

      // Item name with quantity
      data.push(`${item.quantity}x ${itemName}`);
      data.push('\x0A');
      if (secondaryName) {
        data.push(secondaryName);
        data.push('\x0A');
      }
      data.push(separator); // Separator after item name

      // Add modifiers if any
      if (item.modifiers && item.modifiers.length) {
        data.push('\x0A'); // Extra space before modifiers
        const groupedMods = await groupingModifiers(item.product, item.modifiers);
        for (const group of groupedMods) {
          // Add modifier group name
          data.push(`${group.name}:`);
          data.push('\x0A');

          // Add modifiers in this group
          for (const mod of group.modifiers) {
            if (!mod.void_status) {
              data.push(`-> ${mod.name}`);
              data.push('\x0A');
            }
          }
        }
        data.push(separator); // Separator after modifiers
      }

      // Add item notes if exists
      if (item.notes) {
        data.push('\x0A'); // Extra space before notes
        data.push('Notes:');
        data.push('\x0A');
        data.push(item.notes);
        data.push('\x0A');
        data.push(separator); // Separator after notes
      }

      data.push('\x0A'); // Extra space between items
    }

    // Footer section
    data.push(`Cashier: ${currentEmployee ? currentEmployee.name : ''}`);
    data.push('\x0A');
    data.push(`Terminal: ${terminalInfo.name || 'Biyo POS'}`);
    data.push('\x0A');

    // Add form feed to print
    data.push('\x0C');
  }

  // Print using QZ Tray
  // Zebra labels declare their own encoding via ZPL (^CI28), so the charset
  // db_setting only applies to the plain ESC/P branch above.
  let qzOptions = {};
  if(!useZebraLabel){
    const charset = terminalInfo.db_settings?.find(setting => setting.name === 'kitchen_print_charset')?.value;
    if(charset){
      qzOptions = {encoding: charset};
    }
  }
  const config = qz.configs.create(kitchenObj.printerInfo.name, qzOptions);
  await qz.print(config, data).catch(function(e) {
    console.error(e);
    Vue.$log.error('Error printing kitchen label:', e);
  });
}

async function printKitchenReceiptTemplate2 (orderObject, kitchenObj, reason = undefined) {
  Vue.$log.warn(`Print kitchen receipt Template 2 ${reason ? "(" + reason + "}" : "(no reason)"} to `, kitchenObj.printerInfo);

  const terminalInfo = store.getters["config/terminal"];
  const useRedModifiers = shouldUseRedModifiers(terminalInfo, kitchenObj.printerInfo);
  const printer = new Printer();

  // Determine separator length based on printer width
  const separator = '-'.repeat(printer.width);

  // Initialize printer
  printer.buffer.write('\x1B\x40'); // ESC @ - Initialize

  // Header: NEW ORDER or reason
  printer.align("ct");
  printer.cmds += separator + '\n';
  printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
  printer.cmds += printer.buffer.flush();
  if (reason) {
    printer.cmds += `!!! ${reason.toUpperCase()} !!!\n`;
  } else {
    printer.cmds += '!!! NEW ORDER !!!\n';
  }
  printer.buffer.write('\x1B\x21\x00'); // Reset
  printer.cmds += printer.buffer.flush();
  printer.cmds += separator + '\n\n';

  // Left align for content
  printer.align("lt");

  // Dining Option and Receipt Number combined
  printer.buffer.write('\x1B\x21\x00'); // Normal size
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'Receipt #\n';
  printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
  printer.cmds += printer.buffer.flush();

  // Build the receipt value with dining option and receipt number
  let receiptValue = orderObject.id;
  if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    const diningOptionName = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name;
    receiptValue = `${diningOptionName} / ${orderObject.id}`;
  }

  printer.cmds += `${receiptValue}\n\n`;
  printer.buffer.write('\x1B\x21\x00'); // Reset
  printer.cmds += printer.buffer.flush();

  // Customer Name (if available)
  if (orderObject.customer && (orderObject.customer.first_name || orderObject.customer.last_name)) {
    printer.cmds += 'Customer Name\n';
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
    printer.cmds += printer.buffer.flush();
    const customerName = `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim();
    printer.cmds += `${customerName}\n\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();
  }

  // Order Time
  printer.cmds += 'Order Time\n';
  printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
  printer.cmds += printer.buffer.flush();
  const now = new Date();
  const orderDate = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const orderTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  printer.cmds += `${orderDate} - ${orderTime}\n\n`;
  printer.buffer.write('\x1B\x21\x00'); // Reset
  printer.cmds += printer.buffer.flush();

  // Payment status and scheduled prepare time (online orders only)
  if (orderObject.type === 1) {
    const isPaid = orderObject.payments && orderObject.payments.length > 0;
    const prepareBy = orderObject.serve_at
      ? moment(orderObject.serve_at).format("MMM D, h:mm A")
      : "ASAP";

    printer.cmds += 'Payment\n';
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${isPaid ? 'PAID' : 'UNPAID'}\n\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();

    printer.cmds += 'Prepare by\n';
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${prepareBy}\n\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();
  }

  // Table (if any) - only print if table exists and has a name
  if (orderObject.table) {
    const tableObj = store.getters["config/getTableById"](orderObject.table);
    const tableName = tableObj?.table_name;
    if (tableName) {
      printer.cmds += 'Table\n';
      printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
      printer.cmds += printer.buffer.flush();
      printer.cmds += `${tableName}\n\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }
  }

  // Total Items
  const totalItems = kitchenObj.items.reduce((sum, item) => sum + (item.void_status ? 0 : item.quantity), 0);
  printer.cmds += 'Total Items\n';
  printer.buffer.write('\x1B\x21\x38'); // Bold + Double size
  printer.cmds += printer.buffer.flush();
  printer.cmds += `${totalItems} items\n\n`;
  printer.buffer.write('\x1B\x21\x00'); // Reset
  printer.cmds += printer.buffer.flush();

  // Items section
  for (let index = 0; index < kitchenObj.items.length; index++) {
    const item = kitchenObj.items[index];

    // Determine item name (use kitchen_display_name if available)
    const { primaryName: itemName, secondaryName } = resolveKitchenItemNames(item);

    // Separator before item
    printer.cmds += separator + '\n';

    // Item name with quantity - Bold + Double size
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${item.quantity} x ${itemName}\n`;

    if (secondaryName) {
      printer.buffer.write('\x1B\x21\x00'); // Reset to normal size for the secondary name
      printer.cmds += printer.buffer.flush();
      printer.cmds += `${secondaryName}\n`;
    }

    // Modifiers if any — match item size when red (Kitchen impact printer)
    if (item.modifiers && item.modifiers.length > 0) {
      printer.buffer.write(useRedModifiers ? '\x1B\x21\x38' : '\x1B\x21\x20');
      printer.cmds += printer.buffer.flush();

      // Group modifiers properly like in the original template
      const groupedMods = await groupingModifiers(item.product, item.modifiers);
      for (const modifierGroup of groupedMods) {
        // Modifier group name
        printer.cmds += `- ${modifierGroup.name}\n`;

        // Individual modifiers in the group
        for (const modifier of modifierGroup.modifiers) {
          if (!modifier.void_status) {
            if (useRedModifiers) {
              printer.color(1);
            }
            printer.cmds += `${useRedModifiers ? '* ' : '  * '}${modifier.name}\n`;
            if (useRedModifiers) {
              printer.color(0);
            }
          }
        }
      }
    }

    // Special instructions/notes if any
    if (item.notes && item.notes.trim() !== '') {
      printer.buffer.write('\x1B\x21\x20'); // Double Height only
      printer.cmds += printer.buffer.flush();
      printer.cmds += `Note: ${item.notes}\n`;
    }

    // Add spacing between items (except after last item)
    if (index < kitchenObj.items.length - 1) {
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
      printer.cmds += '\n';
    }
  }

  // Final separator after all items
  printer.buffer.write('\x1B\x21\x00'); // Reset to normal
  printer.cmds += printer.buffer.flush();
  printer.cmds += separator + '\n';

  printer.text("");
  printer.align("ct");
  await printer.print(kitchenObj.printerInfo);
  Vue.$log.debug("Printed Kitchen Template 2 =) ");
}

async function printKitchenReceiptTemplate3 (orderObject, kitchenObj, reason = undefined) {
  Vue.$log.warn(`Print kitchen receipt Template 3 ${reason ? "(" + reason + "}" : "(no reason)"} to `, kitchenObj.printerInfo);

  const terminalInfo = store.getters["config/terminal"];
  const useRedModifiers = shouldUseRedModifiers(terminalInfo, kitchenObj.printerInfo);
  const printer = new Printer();

  // Separator is 7 dashes shorter than full width
  const separator = '-'.repeat(Math.max(0, printer.width - 7));

  // Initialize printer
  printer.buffer.write('\x1B\x40'); // ESC @ - Initialize

  // Left align for content
  printer.align("lt");

  // Top section: inline label + value, normal size
  printer.buffer.write('\x1B\x21\x00'); // Normal size
  printer.cmds += printer.buffer.flush();

  // Receipt # (with optional dining option prefix)
  let receiptValue = orderObject.id;
  if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    const diningOptionName = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name;
    receiptValue = `${diningOptionName} / ${orderObject.id}`;
  }
  printer.cmds += `Receipt #: ${receiptValue}\n`;

  // Customer Name (if available)
  if (orderObject.customer && (orderObject.customer.first_name || orderObject.customer.last_name)) {
    const customerName = `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim();
    printer.cmds += `Customer: ${customerName}\n`;
  }

  // Order Time — MM/DD, h:mm AM/PM (no year)
  const now = new Date();
  const orderDate = now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
  const orderTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  printer.cmds += `Order Time: ${orderDate}, ${orderTime}\n`;

  // Payment status and scheduled prepare time (online orders only)
  if (orderObject.type === 1) {
    const isPaid = orderObject.payments && orderObject.payments.length > 0;
    const prepareBy = orderObject.serve_at
      ? moment(orderObject.serve_at).format("MM/DD, hh:mm A")
      : "ASAP";

    // PAID = Bold + Inverse, UNPAID = plain
    if (isPaid) {
      printer.buffer.write('\x1B\x21\x08' + '\x1D\x42\x01'); // Bold + Inverse on
      printer.cmds += printer.buffer.flush();
      printer.cmds += `Payment: PAID\n`;
      printer.buffer.write('\x1D\x42\x00' + '\x1B\x21\x00'); // Inverse off + reset
      printer.cmds += printer.buffer.flush();
    } else {
      printer.cmds += `Payment: UNPAID\n`;
    }

    // Prepare by — always inverse
    printer.buffer.write('\x1D\x42\x01'); // Inverse on
    printer.cmds += printer.buffer.flush();
    printer.cmds += `Prepare by: ${prepareBy}\n`;
    printer.buffer.write('\x1D\x42\x00'); // Inverse off
    printer.cmds += printer.buffer.flush();
  }

  // Table (if any)
  if (orderObject.table) {
    const tableObj = store.getters["config/getTableById"](orderObject.table);
    const tableName = tableObj?.table_name;
    if (tableName) {
      printer.cmds += `Table: ${tableName}\n`;
    }
  }

  // Total Items
  const totalItems = kitchenObj.items.reduce((sum, item) => sum + (item.void_status ? 0 : item.quantity), 0);
  printer.cmds += `Total Items: ${totalItems} items\n`;

  // Items section — same as Template 2
  for (let index = 0; index < kitchenObj.items.length; index++) {
    const item = kitchenObj.items[index];

    const { primaryName: itemName, secondaryName } = resolveKitchenItemNames(item);

    printer.cmds += separator + '\n';

    printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${item.quantity} x ${itemName}\n`;

    if (secondaryName) {
      printer.buffer.write('\x1B\x21\x00'); // Reset to normal size for the secondary name
      printer.cmds += printer.buffer.flush();
      printer.cmds += `${secondaryName}\n`;
    }

    if (item.modifiers && item.modifiers.length > 0) {
      printer.buffer.write(useRedModifiers ? '\x1B\x21\x38' : '\x1B\x21\x20');
      printer.cmds += printer.buffer.flush();

      const groupedMods = await groupingModifiers(item.product, item.modifiers);
      for (const modifierGroup of groupedMods) {
        for (const modifier of modifierGroup.modifiers) {
          if (!modifier.void_status) {
            if (useRedModifiers) {
              printer.color(1);
            }
            printer.cmds += `${useRedModifiers ? '* ' : '  * '}${modifier.name}\n`;
            if (useRedModifiers) {
              printer.color(0);
            }
          }
        }
      }
    }

    if (item.notes && item.notes.trim() !== '') {
      printer.buffer.write('\x1B\x21\x20'); // Double Height only
      printer.cmds += printer.buffer.flush();
      printer.cmds += `Note: ${item.notes}\n`;
    }

    if (index < kitchenObj.items.length - 1) {
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
      printer.cmds += '\n';
    }
  }

  // Final separator
  printer.buffer.write('\x1B\x21\x00'); // Reset to normal
  printer.cmds += printer.buffer.flush();
  printer.cmds += separator + '\n';

  printer.text("");
  printer.align("ct");
  await printer.print(kitchenObj.printerInfo, true, 2);
  Vue.$log.debug("Printed Kitchen Template 3 =) ");
}

/**
 * Title-cases a name for the kitchen ticket: "j & j designs" -> "J & J Designs".
 * Only the first letter of each whitespace-delimited word is upper-cased; the
 * rest is lower-cased and non-letter tokens ("&", numbers) are left untouched.
 */
function toProperCase (str) {
  return String(str || "")
    .toLowerCase()
    .replace(/(^|\s)([a-z])/g, (_match, sep, ch) => sep + ch.toUpperCase());
}

/**
 * Design 4 — one ticket per line item, coffee-shop style.
 *
 *   Item: 1 of 2
 *   Items in order: 2
 *
 *   * Customer Name *          <- Proper Case, only when a customer is attached
 *
 *   Gr Icd Wht Mocha           <- item name, bold + double size
 *   Almond Milk                <- modifiers as a flat list, no group headings
 *   No Whip
 *   Iced
 *
 *   19-May-2022    10:59:34 PM
 *
 *   >TERMINAL NAME<
 *
 * Based on Design 1 (kitchen_print = "1"); drops the Qty/Item table, modifier
 * groups, dining option, store id and register id.
 */
async function printKitchenReceiptTemplate4 (orderObject, kitchenObj, reason = undefined) {
  Vue.$log.warn(`Print kitchen receipt Template 4 ${reason ? "(" + reason + ")" : "(no reason)"} to `, kitchenObj.printerInfo);

  const terminalInfo = store.getters["config/terminal"];
  const useRedModifiers = shouldUseRedModifiers(terminalInfo, kitchenObj.printerInfo);
  const terminalName = String(terminalInfo?.name || "Biyo POS").toUpperCase();

  const items = kitchenObj.items;

  // "Items in order" and each ticket's position count physical units
  // (quantity), not just line items, across the whole order (all
  // printers/stations) — a qty-2 item counts as 2 toward the total, not 1.
  // kitchenObj.items alone can also be a subset of the order (e.g. only
  // newly-added items, or split further by print_per_single_item), so the
  // totals are computed from orderObject.items instead.
  const allOrderKitchenItems = (orderObject.items || []).filter(
    (it) => it.void_status !== 1 && it.quantity > 0 && it.printers && it.printers.length > 0
  );
  const keyOf = (it) => (it.id != null ? `id:${it.id}` : (it.local_id != null ? `lid:${it.local_id}` : null));

  // Starting unit number of each order item (0-based), in order-item sequence.
  // With print_per_single_item on, the SAME source item is split into several
  // independent printer objects/calls (one per unit) — a plain running total
  // computed inside a single call can't tell those apart, since every one of
  // them shares the same item id. Precomputing each item's start offset here
  // lets every call — even for the same item — land on the right absolute
  // position via __kitchenUnitIndex below.
  const startOffsetByKey = new Map();
  let runningQty = 0;
  for (const it of allOrderKitchenItems) {
    const key = keyOf(it);
    if (key) startOffsetByKey.set(key, runningQty);
    runningQty += (it.quantity || 1);
  }
  const itemsInOrder = runningQty || items.reduce((sum, it) => sum + (it.quantity || 1), 0);

  // One physical ticket (with its own cut) per line item — or per unit when
  // the printer has print_per_single_item on (see __kitchenUnitIndex).
  let batchRunningQty = 0;
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    batchRunningQty += (item.quantity || 1);
    const key = keyOf(item);
    // Which unit of this line item this ticket represents: __kitchenUnitIndex
    // when the printer split by quantity, otherwise the item's full quantity
    // (a single ticket covering multiple units completes through the last one).
    const unitIndexWithinItem = item.__kitchenUnitIndex || item.quantity || 1;
    const startOffset = (key && startOffsetByKey.has(key))
      ? startOffsetByKey.get(key)
      : batchRunningQty - (item.quantity || 1);
    const itemPosition = startOffset + unitIndexWithinItem;
    const printer = new Printer();

    printer.buffer.write('\x1B\x40'); // ESC @ - Initialize
    printer.cmds += printer.buffer.flush();
    printer.align("lt");

    // Reason banner only for updates/cancellations (a plain new item has none).
    if (reason) {
      printer.buffer.write('\x1B\x21\x08'); // Bold
      printer.cmds += printer.buffer.flush();
      printer.cmds += `*** ${reason.toUpperCase()} ***\n\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }

    // "ONLINE ORDER" banner — bold black, only for online orders
    if (orderObject.type === 1) {
      printer.color(0); // force black (never red)
      printer.buffer.write('\x1B\x21\x18'); // Bold + Double Height
      printer.cmds += printer.buffer.flush();
      printer.cmds += `ONLINE ORDER\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }

    // Order number — same size as the product name
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
    printer.cmds += printer.buffer.flush();
    printer.cmds += `#${orderObject.id || orderObject.local_id || ''}\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();

    // Dining option, directly under the order number — same size as the modifiers
    if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"]
        && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
      const diningOptionName = terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name;
      printer.buffer.write('\x1B\x21\x10'); // Double Height
      printer.cmds += printer.buffer.flush();
      printer.cmds += `${diningOptionName}\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }
    printer.cmds += '\n';

    // Position within the order — same size as the modifiers
    printer.buffer.write('\x1B\x21\x10'); // Double Height
    printer.cmds += printer.buffer.flush();
    printer.cmds += `Item: ${itemPosition} of ${itemsInOrder}\n`;
    printer.cmds += `Items in order: ${itemsInOrder}\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();
    printer.cmds += '\n';

    // Customer name — Proper Case, wrapped in "* *", same size as the product name
    if (orderObject.customer && (orderObject.customer.first_name || orderObject.customer.last_name)) {
      const rawName = `${orderObject.customer.first_name || ''} ${orderObject.customer.last_name || ''}`.trim();
      printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
      printer.cmds += printer.buffer.flush();
      printer.cmds += `* ${toProperCase(rawName)} *\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
      printer.cmds += '\n';
    }

    // Payment status + prepare time for online orders (kept from Design 1)
    if (orderObject.type === 1) {
      const isPaid = orderObject.payments && orderObject.payments.length > 0;
      const prepareBy = orderObject.serve_at
        ? moment(orderObject.serve_at).format("MMM D, h:mm A")
        : "ASAP";
      printer.cmds += `${isPaid ? "*** PAID ***" : "*** UNPAID ***"}\n`;
      printer.cmds += `Prepare by: ${prepareBy}\n\n`;
    }

    // Item name — bold + double height + double width (use kitchen_display_name)
    const { primaryName: itemName, secondaryName } = resolveKitchenItemNames(item);
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${item.quantity > 1 ? item.quantity + " x " : ""}${itemName}\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();
    if (secondaryName) {
      printer.cmds += `${secondaryName}\n`;
    }

    // Modifiers — flat list, no group names, one step up from normal (double height)
    if (item.modifiers && item.modifiers.length > 0) {
      printer.buffer.write('\x1B\x21\x10'); // Double Height
      printer.cmds += printer.buffer.flush();
      for (const modifier of item.modifiers) {
        if (!modifier.void_status) {
          if (useRedModifiers) {
            printer.color(1);
          }
          printer.cmds += `${modifier.name}\n`;
          if (useRedModifiers) {
            printer.color(0);
          }
        }
      }
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }

    // Item note — same size as the modifiers
    if (item.notes && item.notes.trim() !== '') {
      printer.buffer.write('\x1B\x21\x10'); // Double Height
      printer.cmds += printer.buffer.flush();
      printer.cmds += `!!! ${item.notes.trim()} !!!\n`;
      printer.buffer.write('\x1B\x21\x00'); // Reset
      printer.cmds += printer.buffer.flush();
    }

    printer.cmds += '\n\n';

    // Date / time — left aligned, single line
    const now = moment();
    printer.cmds += `${now.format("DD-MMM-YYYY")}  ${now.format("hh:mm:ss A")}\n`;

    printer.cmds += '\n\n';

    // Terminal name marker — same size as the product name
    printer.buffer.write('\x1B\x21\x38'); // Bold + Double Height + Double Width
    printer.cmds += printer.buffer.flush();
    printer.cmds += `>${terminalName}<\n`;
    printer.buffer.write('\x1B\x21\x00'); // Reset
    printer.cmds += printer.buffer.flush();

    printer.text("");
    await printer.print(kitchenObj.printerInfo, true, 3);
  }

  Vue.$log.debug("Printed Kitchen Template 4 =) ");
}

async function printKitchenReceipt (orderObject, kitchenObj, reason = undefined) {
  if (kitchenObj.printerInfo.design_template === 'kitchen_label') {
    // Use ESC/P for kitchen labels
    return await printKitchenLabelESCP(kitchenObj, orderObject, reason);
  }

  const terminalInfo = store.getters["config/terminal"];

  // Check kitchen_print setting
  const kitchenPrintSetting = terminalInfo.db_settings?.find(setting => setting.name === "kitchen_print");
  const kitchenPrintType = kitchenPrintSetting?.value ?? "1";

  if (kitchenPrintType === "2") {
    return await printKitchenReceiptTemplate2(orderObject, kitchenObj, reason);
  }

  if (kitchenPrintType === "3") {
    return await printKitchenReceiptTemplate3(orderObject, kitchenObj, reason);
  }

  if (kitchenPrintType === "4") {
    return await printKitchenReceiptTemplate4(orderObject, kitchenObj, reason);
  }

  // Original ESC/POS code for other kitchen receipts (kitchen_print = 1 or default)
  Vue.$log.warn(`Print kitchen receipt ${reason ? "(" + reason + "}" : "(no reason)"} to `, kitchenObj.printerInfo);
  let printer;
  if (kitchenObj.printerInfo.design_template === 'kitchen_label') {
    // Use 2.4 inch (61mm) width for kitchen labels
    printer = new Printer(false, true);
  } else {
    printer = new Printer();
  }
  printer.line();
  if (reason) {
    printer.tableCustom([
      { text: `!!! ${reason.toUpperCase()} !!!`, align: "CENTER", width: 1 }
    ]);
  } else {
    printer.tableCustom([
      { text: "!!! NEW ITEM !!!", align: "CENTER", width: 1 }
    ]);
  }

  printer.line();
  const currentEmployee = store.getters.employee;
  printer.align("ct");

  // Get table name if table exists and has a name
  let tableDisplayText = "";
  if (orderObject.table) {
    const tableObj = store.getters["config/getTableById"](orderObject.table);
    const tableName = tableObj?.table_name;
    if (tableName) {
      tableDisplayText = "/" + tableName;
    }
  }

  printer.tableCustom([
    {
      text: `Receipt#: ${orderObject.id}${tableDisplayText}`,
      align: "LEFT",
      width: 1
    }]);
  if ((await isDiningOption()) && orderObject.dining_option_type && terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type)) {
    printer.tableCustom([
      {
        text: `Dining Option: ${terminalInfo["dining_options"].find((option) => option.id === orderObject.dining_option_type).name}`,
        align: "CENTER",
        width: 1
      }]);
  }
  if (orderObject.type === 1) {
    const isPaid = orderObject.payments && orderObject.payments.length > 0;
    const prepareBy = orderObject.serve_at
      ? moment(orderObject.serve_at).format("MMM D, h:mm A")
      : "ASAP";
    printer.tableCustom([
      { text: isPaid ? "*** PAID ***" : "*** UNPAID ***", align: "CENTER", width: 1 }
    ]);
    printer.tableCustom([
      { text: `Prepare by: ${prepareBy}`, align: "CENTER", width: 1 }
    ]);
  }
  printer.text("");
  await buildDateTimeCashierPart(printer, currentEmployee, orderObject.table);
  printer.text("");
  if (reason == "item canceled") {
    await buildItemsPart(printer, kitchenObj.items, true, true, kitchenObj.printerInfo);
  } else {
    await buildItemsPart(printer, kitchenObj.items, true, false, kitchenObj.printerInfo);
  }

  printer.text("");
  printer.align("ct");
  await printer.print(kitchenObj.printerInfo);
  Vue.$log.debug("Printed =) ");
}

export async function printSummary (isSaleSummary = false, daySummary = null) {
  const terminalInfo = store.getters["config/terminal"];
  const printer = new Printer(!!terminalInfo.printer["is_58mm"]);
  const currentShift = store.getters["shift"];
  const reportType = isSaleSummary ? "s" : currentShift.shift_close_date ? "z" : "x";

  // title
  let title = "Sale Summary";
  if (reportType === "x") {
    title = "Shift X Report";
  } else if (reportType === "z") {
    title = "Shift Z Report";
    title = "Shift Z Report";
  }
  printer.title(title, true);
  printer.priceRow("", "");

  // STORE INFO
  const storeInfo = store.getters["config/store"];
  // defining localization variables
  const localization = store.getters["config/isLocalization"]
  const localizedFormat = "MM/DD/YYYY hh:mm A"
  const deLocalizedFormat = "DD/MM/YYYY hh:mm A"
  printer
    .tableCustom([
      { text: `Store: ${storeInfo.name}`, align: "LEFT", width: 1 }]);
  if (!isSaleSummary) {
    printer
      .tableCustom([
        { text: `Terminal: ${terminalInfo.name}`, align: "LEFT", width: 1 }]);
  }
  printer.line();
  let start = moment(moment().format("YYYY-MM-DDT") + storeInfo.start_time);
  if (isSaleSummary) {
    const now = moment();
    if (now < start) {
      start = start.subtract(1, "days");
    }
    // localizing date format if country is US
    printer.priceRow("From", start.format(localization ? localizedFormat : deLocalizedFormat), false, [0.3, 0.7]);
    printer.priceRow("", "");
    // localizing date format if country is US
    printer.priceRow("To", now.format(localization ? localizedFormat : deLocalizedFormat), false, [0.3, 0.7]);

  } else {
    // SHIFT
    const emplOpen = store.getters["config/employeeById"](currentShift.open_shift_employee);
    printer
      .tableCustom([
        { text: "Shift opened:", align: "LEFT", width: 1 }]);
    // localizing date format if country is US
    const openDate = moment(Date.parse(currentShift.shift_open_date)).format(localization ? localizedFormat : deLocalizedFormat);
    printer
      .tableCustom([
        { text: emplOpen.name, align: "LEFT", width: 0.5 },
        { text: openDate, align: "RIGHT", width: 0.5 }
      ]);

    //printing line
    printer.line();

    // printing starting cash amount
    printer.tableCustom([
      { text: 'Cash Starting Amount', align: "LEFT", width: 1 },
      { text: `$ ${currentShift.opening_amount.toFixed(2)}`, align:"RIGHT", width: 1}
    ])

    switch (reportType) {
      case 'x':
        printXReport(printer);
        break;
      case 'z':
        printZReport(printer)
        break;
      default:
        break;
    }

    /* <<<<<<<< PRIOR CODE >>>>>>>>
    if (reportType === "z") {
      const emplClosed = store.getters["config/employeeById"](currentShift.close_shift_employee);
      // localizing date format if country is US
      const closeDate = moment(Date.parse(currentShift.shift_close_date)).format(localization ? localizedFormat : deLocalizedFormat);
      printer.priceRow("", "");
      printer.priceRow("Shift closed:", "");

      printer
        .tableCustom([
          { text: emplClosed.name, align: "LEFT", width: 0.5 },
          { text: closeDate, align: "RIGHT", width: 0.5 }
        ]);
    }
    */
  }

  // CASH DRAWER
 /* if (!isSaleSummary) {

   <<<<<<<<< PRIOR CODE >>>>>>>>>
    printer.line();
    printer
      .tableCustom([
        { text: "Cash Drawer", align: "CENTER", width: 1 }
      ]);

    printer.line();
    printer.priceRow("Opening Amount", currentShift.opening_amount.toFixed(2));
    printer.priceRow("Total Sales", currentShift.total_cashtenders.toFixed(2));
    // PAYMENTS
    let payments = [];
    if (currentShift.payment_types) {
      payments = currentShift.payment_types;
    }
    if (payments.length > 0) {
      printer.line();
      printer
        .tableCustom([
          { text: "Payments", align: "CENTER", width: 1 }
        ]);
      printer.line();
      payments.forEach(function (payment) {
        printer.priceRow(payment.name, payment.sum.toFixed(2));
      });
    }

    printer.line();
    const safeDrop = 0;
    const paidIn = 0;
    const paidOut = 0;
    // Expected amount = Opening amount + Cash Sales - cash returns - safe drop + payin - pay out
    const expectedAmount = currentShift.opening_amount + currentShift.total_cashtenders - currentShift.total_cashreturns
      - safeDrop + paidIn - paidOut;
    printer.priceRow("Expected Amount", expectedAmount.toFixed(2), true);
    if (reportType === "z") {
      printer.priceRow("Closing amount", currentShift.closing_amount.toFixed(2));
      const diff = currentShift.closing_amount - expectedAmount;
      printer.priceRow("Difference", diff.toFixed(2), true);
    }

  }*/
  // SALE SUMMARY
  if (isSaleSummary) {
    printer.line();
    printer.title("Sale Summary", false);
    printer.line();

    const saleSummary = {
      grossSales: 0,
      discounts: 0,
      taxes: 0,
      netSales: 0
    };

    const orders = store.getters["orders/getAllOrders"];
    for (const order of orders) {
      // todo check datetime filter
      if (moment(order.open_date).unix() > start.unix() > start.unix()) {
        saleSummary.discounts += order.discount_total;
        saleSummary.taxes += order.tax_total;
      }
    }
    printer.priceRow("Net sales", daySummary["sale_summary"].net_sales.toFixed(2), true);
    printer.priceRow("Refunds", daySummary["sale_summary"].refunds.toFixed(2), false);
    printer.priceRow("Discounts", daySummary["sale_summary"].discounts.toFixed(2), false);
    printer.priceRow("Taxes", daySummary["sale_summary"].taxes.toFixed(2), false);
    printer.priceRow("Gross Sales", daySummary["sale_summary"].gross_sales.toFixed(2), true);

    // PAYMENTS
    const payments = daySummary.payments;
    if (payments.length > 0) {
      printer.line();
      printer
        .tableCustom([
          { text: "Payments", align: "CENTER", width: 1 }
        ]);
      printer.line();
      payments.forEach(function (payment) {
        printer.priceRow(payment.name, payment.sum.toFixed(2));
      });
    }
  }

  // ORDERS
  const orders = isSaleSummary ? daySummary["orders_count"] : {};
  if (Object.keys(orders).length > 0) {
    printer.line();
    printer
      .tableCustom([
        { text: "Orders", align: "CENTER", width: 1 }
      ]);
    printer.line();
    for (const [key, value] of Object.entries(orders)) {
      printer.priceRow(key, value);
    }
  }

  // END
  printer.line();
  printer.title(`End of ${title}`, true);

  await printer.print();
  Vue.$log.debug("Printed =) ");
}

const printBreakdowns = (printer) => {
  const currentShift = store.getters["shift"];

  // printing total net sales
  printer.priceRow('Total Net Sales',`$ ${currentShift.payment_breakdown.net_sales.toFixed(2)}`)

  //printing line
  printer.line();

  // printing sales breakdown header
  printer.tableCustom([
    {text:'Sales Breakdown', align:"CENTER", width: 1}
  ])

  //printing line
  printer.line();

  // printing sales breakdown
  const sales = currentShift.payment_breakdown.sales
  sales.forEach(sale => {
    printer.priceRow(`${sale.payment_type}`,`$ ${sale.sum.toFixed(2)}`)
  })

  //printing line
  printer.line();

  // printing refunds breakdown header
  printer.tableCustom([
    {text:'Refunds Breakdown', align:"CENTER", width: 1}
  ])

  //printing line
  printer.line();

  // printing refunds breakdown
  const refunds = currentShift.payment_breakdown.refund
  refunds.forEach(refund => {
    printer.priceRow(`${refund.payment_type}`,`- $ ${refund.sum.toFixed(2)}`)
  })


}

const printXReport = (printer) => {
  //printing line
  printer.line();

  // print total_net_sales and sales and refund breakdowns
  printBreakdowns(printer)

}

const printZReport = (printer) => {
  const currentShift = store.getters["shift"];

  // report type z
  printer.line();
  const safeDrop = 0;
  const paidIn = 0;
  const paidOut = 0;
  const expectedAmount = currentShift.opening_amount + currentShift.payment_breakdown.net_sales
    - safeDrop + paidIn - paidOut;
  printer.priceRow("Expected Cash Amount", `$ ${expectedAmount.toFixed(2)}`);
  printer.priceRow("Cash Closing Amount", `$ ${currentShift.closing_amount.toFixed(2)}`);
  //printing line
  printer.line();
  const diff = currentShift.closing_amount - expectedAmount;
  const isCashOver = diff > 0 ? true : false
  printer.priceRow(`Cash ${isCashOver ? 'is Over' : 'Difference'}`, `${isCashOver ? '' : '-'} $ ${isCashOver ? diff.toFixed(2) : (-1* diff.toFixed(2))}`);

  //printing line
  printer.line()

  // print total_net_sales and sales and refund breakdowns
  printBreakdowns(printer)
}

export async function openDrawer () {
  try {
    const printer = new Printer();
    printer.openDrawerCommand();
    await printer.send();
  } catch (e) {
    return false;
  }

}

/**
 * Prints a font/style/size sampler so you can see exactly how each
 * ESC/POS combination renders on this specific printer.
 */
export async function printFontStyleTest () {
  const terminalInfo = store.getters["config/terminal"];
  const printer = new Printer(!!(terminalInfo.printer && terminalInfo.printer["is_58mm"]));
  const sep = '='.repeat(printer.width);
  const dash = '-'.repeat(printer.width);

  const reset  = '\x1B\x21\x00'; // Font A, normal
  const fontB  = '\x1B\x21\x01'; // Font B (smaller/condensed)
  const bold   = '\x1B\x21\x08'; // Bold only
  const dh     = '\x1B\x21\x10'; // Double-height only
  const dw     = '\x1B\x21\x20'; // Double-width only
  const boldDh = '\x1B\x21\x18'; // Bold + Double-height
  const boldDw = '\x1B\x21\x28'; // Bold + Double-width
  const dhDw   = '\x1B\x21\x30'; // Double-height + Double-width
  const boldDhDw = '\x1B\x21\x38'; // Bold + Double-height + Double-width
  const fontBBold = '\x1B\x21\x09'; // Font B + Bold

  const sample = 'The quick brown fox 0123456789';

  const w = (cmd, label, text) => {
    printer.buffer.write(cmd);
    printer.cmds += printer.buffer.flush();
    printer.cmds += `${label}: ${text}\n`;
  };

  // Header
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.align('ct');
  printer.cmds += sep + '\n';
  printer.cmds += 'FONT & STYLE TEST PAGE\n';
  printer.cmds += sep + '\n\n';
  printer.align('lt');

  // --- Font A styles ---
  printer.cmds += dash + '\n';
  printer.cmds += 'FONT A - STYLES\n';
  printer.cmds += dash + '\n';
  w(reset,    'Normal     ', sample);
  w(bold,     'Bold       ', sample);
  w(fontB,    'Font B     ', sample);
  w(fontBBold,'Font B Bold', sample);

  // Underline via style helper
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.style('U');
  printer.cmds += `Underline  : ${sample}\n`;
  printer.style('U2');
  printer.cmds += `Underline2 : ${sample}\n`;
  printer.style('B');
  printer.style('U');
  printer.cmds += `Bold+Uline : ${sample}\n`;
  printer.style('NORMAL');
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.cmds += '\n';

  // --- Sizes ---
  printer.cmds += dash + '\n';
  printer.cmds += 'FONT A - SIZES\n';
  printer.cmds += dash + '\n';
  w(reset,     '[1x ]  Normal      ', sample);
  w(dh,        '[x2H]  Dbl Height  ', sample);
  w(dw,        '[x2W]  Dbl Width   ', sample);
  w(boldDh,    '[B+2H] Bold+DblH   ', sample);
  w(boldDw,    '[B+2W] Bold+DblW   ', sample);
  w(dhDw,      '[2H+2W] DblH+DblW ', sample);
  w(boldDhDw,  '[B+2H+2W] All     ', sample);
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.cmds += '\n';

  // --- Inverse / Highlight ---
  const invOn  = '\x1D\x42\x01'; // GS B 1 — reverse video on  (white on black)
  const invOff = '\x1D\x42\x00'; // GS B 0 — reverse video off

  printer.cmds += dash + '\n';
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'INVERSE / HIGHLIGHT\n';
  printer.cmds += dash + '\n';

  // Normal inverse
  printer.buffer.write(invOn);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'Normal + Inverse (highlight)\n';
  printer.buffer.write(invOff);
  printer.cmds += printer.buffer.flush();

  // Bold inverse
  printer.buffer.write(bold + invOn);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'Bold + Inverse\n';
  printer.buffer.write(invOff + reset);
  printer.cmds += printer.buffer.flush();

  // Double-height inverse
  printer.buffer.write(dh + invOn);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'Double-Height + Inverse\n';
  printer.buffer.write(invOff + reset);
  printer.cmds += printer.buffer.flush();

  // Bold + Double-height + Double-width inverse (maximum)
  printer.buffer.write(boldDhDw + invOn);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'Bold+2H+2W+Inverse\n';
  printer.buffer.write(invOff + reset);
  printer.cmds += printer.buffer.flush();

  printer.cmds += '\n';

  // --- Alignment ---
  printer.cmds += dash + '\n';
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.cmds += 'ALIGNMENT\n';
  printer.cmds += dash + '\n';
  printer.align('lt');
  printer.cmds += 'LEFT aligned text\n';
  printer.align('ct');
  printer.cmds += 'CENTER aligned text\n';
  printer.align('rt');
  printer.cmds += 'RIGHT aligned text\n';
  printer.align('lt');
  printer.cmds += '\n';

  // --- Character Spacing ---
  // ESC SP n — adds n extra dots of space after each character (0 = default)
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.cmds += dash + '\n';
  printer.cmds += 'CHARACTER SPACING (ESC SP n)\n';
  printer.cmds += dash + '\n';

  for (const spacing of [0, 2, 4, 8, 12]) {
    printer.buffer.write('\x1B\x20' + String.fromCharCode(spacing));
    printer.cmds += printer.buffer.flush();
    printer.cmds += `SP=${spacing}: AaBbCcDdEeFfGg 0123\n`;
  }
  // Reset character spacing to default
  printer.buffer.write('\x1B\x20\x00');
  printer.cmds += printer.buffer.flush();
  printer.cmds += '\n';

  // --- Line Spacing ---
  // ESC 3 n — sets line spacing to n dots (24 = default on most printers)
  printer.cmds += dash + '\n';
  printer.cmds += 'LINE SPACING (ESC 3 n)\n';
  printer.cmds += dash + '\n';

  for (const spacing of [16, 24, 32, 48]) {
    printer.buffer.write('\x1B\x33' + String.fromCharCode(spacing));
    printer.cmds += printer.buffer.flush();
    printer.cmds += `LS=${spacing} dots\n`;
    printer.cmds += `The quick brown fox\n`;
  }
  // Reset line spacing to default
  printer.buffer.write('\x1B\x32');
  printer.cmds += printer.buffer.flush();
  printer.cmds += '\n';

  // Footer
  printer.buffer.write(reset);
  printer.cmds += printer.buffer.flush();
  printer.align('ct');
  printer.cmds += sep + '\n';
  printer.cmds += 'END OF TEST\n';
  printer.cmds += sep + '\n';

  printer.text('');
  await printer.print();
}
